/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  Shield, 
  MapPin, 
  Activity, 
  Plus, 
  UserPlus, 
  Check, 
  CheckSquare, 
  Square, 
  Briefcase, 
  Save, 
  Mail, 
  Lock,
  FileText,
  AlertCircle,
  MessageCircle,
  Phone,
  Edit2,
  Trash2,
  RotateCcw,
  HelpCircle,
  Navigation,
  Compass,
  Route,
  Building2,
  Search,
  X,
  ArrowRight,
  Sparkles,
  Layers,
  Globe,
  Building,
  RefreshCw
} from 'lucide-react';
import { Client, CostCenter, Destination, ClientRouteInfo, UserProfile, ActivityLog, UserRole, SupportArea, isClientRole, isInternalRole, isAdminRole } from '../types.ts';
import { dataService } from '../lib/dataService.ts';

export const ALL_MENU_OPTIONS = [
  // Menús Principales
  { key: 'welcome', label: 'Inicio / Bienvenida', category: 'General' },
  { key: 'kpis', label: 'KPIS - Conteo y Eficiencia Operativa (Meta 100%)', category: 'General' },
  { key: 'dashboard', label: 'Tablero de Control Global', category: 'General' },
  { key: 'calendar', label: 'Plan de Operación (Calendario y Rastreo Live)', category: 'General' },
  { key: 'meetings', label: 'Sala de Meet Virtual', category: 'General' },
  { key: 'admin_portal', label: 'Panel Control Admin', category: 'General' },
  { key: 'clients', label: 'Socio Comercial / Configuración', category: 'General' },

  // Submenús del Socio/Cliente
  { key: 'client_dashboard', label: 'Socio - Tablero de Control', category: 'Socio Cliente' },
  { key: 'client_catalog', label: 'Socio - Catálogo de Mercancías', category: 'Socio Cliente' },
  { key: 'client_cargas_envios', label: 'Socio - Mis Cargas y Envíos', category: 'Socio Cliente' },
  { key: 'client_trazabilidad', label: 'Socio - Trazabilidad de Folios', category: 'Socio Cliente' },
  { key: 'client_facturas_cobros', label: 'Socio - Mis Facturas / Cobros', category: 'Socio Cliente' },
  { key: 'client_historial', label: 'Socio - Historial de Folios', category: 'Socio Cliente' },
  { key: 'client_reprocesos', label: 'Socio - Reprocesos / Devoluciones', category: 'Socio Cliente' },
  { key: 'client_kpis', label: 'Socio - KPIS & Eficiencia Operativa (Meta 100%)', category: 'Socio Cliente' },
  { key: 'client_alertas', label: 'Socio - Alertas y Avisos', category: 'Socio Cliente' },
  { key: 'client_chat_soporte', label: 'Socio - Chat de Soporte / WhatsApp', category: 'Socio Cliente' },

  // Submenús Internos (8 en total)
  { key: 'internal_proyectos', label: 'Interno - Proyectos', category: 'Interno Staff' },
  { key: 'internal_seguridad', label: 'Interno - Seguridad', category: 'Interno Staff' },
  { key: 'internal_operaciones', label: 'Interno - Operaciones', category: 'Interno Staff' },
  { key: 'internal_rrhh', label: 'Interno - Recursos Humanos', category: 'Interno Staff' },
  { key: 'internal_almacen', label: 'Interno - Almacén', category: 'Interno Staff' },
  { key: 'internal_activos', label: 'Interno - Activos', category: 'Interno Staff' },
  { key: 'internal_mantenimiento', label: 'Interno - Mantenimiento', category: 'Interno Staff' },
  { key: 'internal_it_sistemas', label: 'Interno - IT-Sistemas', category: 'Interno Staff' },
];

interface AdminPortalProps {
  clients: Client[];
  costCenters: CostCenter[];
  destinations?: Destination[];
  onAddClient: (client: Client) => void;
  onAddCostCenter: (cc: CostCenter) => void;
  onUpdateClient?: (client: Client) => void;
  onDeleteClient?: (id: string) => void;
  onUpdateCostCenter?: (cc: CostCenter) => void;
  onDeleteCostCenter?: (id: string) => void;
  onAddDestination?: (dest: Destination) => void;
  onUpdateDestination?: (dest: Destination) => void;
  onDeleteDestination?: (id: string) => void;
  currentUser: { email: string; name: string; role: string };
  onLogActivity: (action: string, details: string) => void;
  onResetDatabase?: () => Promise<void>;
}

export default function AdminPortal({
  clients,
  costCenters,
  destinations = [],
  onAddClient,
  onAddCostCenter,
  onUpdateClient,
  onDeleteClient,
  onUpdateCostCenter,
  onDeleteCostCenter,
  onAddDestination,
  onUpdateDestination,
  onDeleteDestination,
  currentUser,
  onLogActivity,
  onResetDatabase
}: AdminPortalProps) {
  const [activeSubTab, setActiveSubTab] = useState<'users' | 'clients' | 'costCenters' | 'destinos' | 'activity' | 'database' | 'whatsapp'>('users');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // States for Destination Management
  const [editingDestination, setEditingDestination] = useState<Destination | null>(null);
  const [destName, setDestName] = useState('');
  const [destClientId, setDestClientId] = useState('');
  const [destSelectedClientIds, setDestSelectedClientIds] = useState<string[]>([]);
  const [destAddress, setDestAddress] = useState('');
  const [destLat, setDestLat] = useState<number>(25.6866);
  const [destLng, setDestLng] = useState<number>(-100.3161);
  const [destRouteNotes, setDestRouteNotes] = useState('');
  const [destEstimatedKm, setDestEstimatedKm] = useState<number>(45);
  const [destEstimatedMins, setDestEstimatedMins] = useState<number>(40);
  const [destClientFilter, setDestClientFilter] = useState<string>('ALL');
  const [destSearchTerm, setDestSearchTerm] = useState<string>('');

  const toggleDestSelectedClient = (clientId: string) => {
    setDestSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // States for WhatsApp support configuration
  const [supportAreas, setSupportAreas] = useState<SupportArea[]>([]);

  // Editing/creation states for dynamic areas
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null);
  const [areaName, setAreaName] = useState('');
  const [areaAgent, setAreaAgent] = useState('');
  const [areaPhone, setAreaPhone] = useState('');
  const [areaDesc, setAreaDesc] = useState('');
  const [areaColor, setAreaColor] = useState('emerald');
  const [areaSelectedClientIds, setAreaSelectedClientIds] = useState<string[]>([]);
  const [areaAllClients, setAreaAllClients] = useState<boolean>(true);
  const [areaCostCenterIds, setAreaCostCenterIds] = useState<string[]>([]);
  const [areaAllCostCenters, setAreaAllCostCenters] = useState<boolean>(true);
  const [previewFilterCcId, setPreviewFilterCcId] = useState<string>('ALL');
  const [previewFilterClientId, setPreviewFilterClientId] = useState<string>('ALL');

  // Editing states
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingCostCenter, setEditingCostCenter] = useState<CostCenter | null>(null);

  // New User Form State
  const [uName, setUName] = useState('');
  const [uEmail, setUEmail] = useState('');
  const [uRole, setURole] = useState<UserRole>('OPERADOR');
  const [uClientCode, setUClientCode] = useState('');
  const [uAssignedClientIds, setUAssignedClientIds] = useState<string[]>([]);
  const [uAssignedCostCenterIds, setUAssignedCostCenterIds] = useState<string[]>([]);
  const [uPassword, setUPassword] = useState('demo1234');

  const toggleAssignedClient = (clientIdOrCode: string) => {
    setUAssignedClientIds(prev =>
      prev.includes(clientIdOrCode)
        ? prev.filter(id => id !== clientIdOrCode)
        : [...prev, clientIdOrCode]
    );
  };

  const toggleAssignedCostCenter = (ccIdOrName: string) => {
    setUAssignedCostCenterIds(prev =>
      prev.includes(ccIdOrName)
        ? prev.filter(id => id !== ccIdOrName)
        : [...prev, ccIdOrName]
    );
  };
  const [uPerms, setUPerms] = useState<{
    canCreateFolios: boolean;
    canUpdateStages: boolean;
    canViewDashboard: boolean;
    canManageUsers: boolean;
    canBilling: boolean;
    menuAccess?: Record<string, boolean>;
  }>({
    canCreateFolios: true,
    canUpdateStages: true,
    canViewDashboard: true,
    canManageUsers: false,
    canBilling: true,
    menuAccess: {},
  });

  // Client Creation in Admin Form State (with cost center assignment)
  const [cCode, setCCode] = useState('');
  const [cName, setCName] = useState('');
  const [cRfc, setCRfc] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cAddress, setCAddress] = useState('');
  const [assignCcOption, setAssignCcOption] = useState<'none' | 'existing' | 'new'>('none');
  const [selectedCcId, setSelectedCcId] = useState('');
  const [newCcCode, setNewCcCode] = useState('');
  const [newCcName, setNewCcName] = useState('');
  const [newCcManager, setNewCcManager] = useState('');
  const [newCcAddress, setNewCcAddress] = useState('');

  // Cost Center Form State
  const [ccClientId, setCcClientId] = useState('');
  const [ccSelectedClientIds, setCcSelectedClientIds] = useState<string[]>([]);
  const [ccCode, setCcCode] = useState('');
  const [ccName, setCcName] = useState('');
  const [ccManager, setCcManager] = useState('');
  const [ccAddress, setCcAddress] = useState('');
  const [ccLat, setCcLat] = useState<number | string>(25.6866);
  const [ccLng, setCcLng] = useState<number | string>(-100.3161);

  const toggleCcSelectedClient = (clientId: string) => {
    setCcSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Fetch admin-related data
  useEffect(() => {
    let active = true;
    const unsubUsers = dataService.subscribeToUsers((usersData) => {
      if (active) {
        setUsers(usersData);
        setIsLoading(false);
      }
    });

    async function fetchLogsData() {
      try {
        const logsData = await dataService.getActivityLogs();
        if (active) {
          setLogs(logsData.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
        }
      } catch (err) {
        console.error('Failed to load admin logs', err);
      }
    }
    fetchLogsData();

    return () => {
      active = false;
      unsubUsers();
    };
  }, [clients, costCenters]);

  // Load WhatsApp settings on mount
  useEffect(() => {
    const savedList = localStorage.getItem('snfsic_whatsapp_support_areas');
    let areas: SupportArea[] = [];
    if (savedList) {
      try {
        const parsed = JSON.parse(savedList);
        if (Array.isArray(parsed) && parsed.length > 0) {
          areas = parsed;
        }
      } catch (e) {
        console.error("Error parsing saved support areas", e);
      }
    }

    if (areas.length === 0) {
      // Fallback legacy style or default
      const legacy = localStorage.getItem('snfsic_whatsapp_support');
      let facturacionAgentVal = 'Lic. Ana Karen Silva';
      let facturacionPhoneVal = '8712397902';
      let almacenAgentVal = 'Ing. Mateo Rodríguez';
      let almacenPhoneVal = '4622996955';
      if (legacy) {
        try {
          const parsed = JSON.parse(legacy);
          if (parsed.facturacion?.agent) facturacionAgentVal = parsed.facturacion.agent;
          if (parsed.facturacion?.phone) facturacionPhoneVal = parsed.facturacion.phone;
          if (parsed.almacen?.agent) almacenAgentVal = parsed.almacen.agent;
          if (parsed.almacen?.phone) almacenPhoneVal = parsed.almacen.phone;
        } catch (e) {
          console.error(e);
        }
      }
      areas = [
        {
          id: 'facturacion',
          name: 'Departamento de Facturación',
          agent: facturacionAgentVal,
          phone: facturacionPhoneVal,
          desc: 'Dudas de cobranza, prefacturas, complementos de pago y refacturaciones.',
          color: 'emerald'
        },
        {
          id: 'almacen',
          name: 'Control de Almacén y Logística',
          agent: almacenAgentVal,
          phone: almacenPhoneVal,
          desc: 'Estado físico de piezas, recepción de material, granallado y despacho.',
          color: 'blue'
        }
      ];
    }
    setSupportAreas(areas);
  }, []);

  // Sync editing fields with state variables
  useEffect(() => {
    if (editingUser) {
      setUName(editingUser.name);
      setUEmail(editingUser.email);
      setURole(editingUser.role);
      setUClientCode(editingUser.clientCode || '');
      setUAssignedClientIds(editingUser.assignedClientIds || (editingUser.clientCode ? [editingUser.clientCode] : []));
      setUAssignedCostCenterIds(editingUser.assignedCostCenterIds || []);
      setUPassword(editingUser.password || 'demo1234');
      setUPerms({
        canCreateFolios: editingUser.permissions.canCreateFolios,
        canUpdateStages: editingUser.permissions.canUpdateStages,
        canViewDashboard: editingUser.permissions.canViewDashboard,
        canManageUsers: editingUser.permissions.canManageUsers,
        canBilling: editingUser.permissions.canBilling,
        menuAccess: editingUser.permissions.menuAccess || {},
      });
    } else {
      setUName('');
      setUEmail('');
      setURole('OPERADOR');
      setUClientCode('');
      setUAssignedClientIds([]);
      setUAssignedCostCenterIds([]);
      setUPassword('demo1234');
      setUPerms({
        canCreateFolios: true,
        canUpdateStages: true,
        canViewDashboard: true,
        canManageUsers: false,
        canBilling: true,
        menuAccess: {},
      });
    }
  }, [editingUser]);

  useEffect(() => {
    if (editingClient) {
      setCCode(editingClient.code);
      setCName(editingClient.name);
      setCRfc(editingClient.rfc);
      setCEmail(editingClient.contactEmail || '');
      setCPhone(editingClient.contactPhone || '');
      setCAddress(editingClient.address || '');
    } else {
      setCCode('');
      setCName('');
      setCRfc('');
      setCEmail('');
      setCPhone('');
      setCAddress('');
      setAssignCcOption('none');
      setSelectedCcId('');
      setNewCcCode('');
      setNewCcName('');
      setNewCcManager('');
      setNewCcAddress('');
    }
  }, [editingClient]);

  useEffect(() => {
    if (editingCostCenter) {
      const initialClientIds = editingCostCenter.clientIds && editingCostCenter.clientIds.length > 0
        ? editingCostCenter.clientIds
        : (editingCostCenter.clientId ? [editingCostCenter.clientId] : []);
      setCcSelectedClientIds(initialClientIds);
      setCcClientId(editingCostCenter.clientId || (initialClientIds[0] || ''));
      setCcCode(editingCostCenter.code);
      setCcName(editingCostCenter.name);
      setCcManager(editingCostCenter.manager || '');
      setCcAddress(editingCostCenter.address || '');
      setCcLat(editingCostCenter.lat ?? 25.6866);
      setCcLng(editingCostCenter.lng ?? -100.3161);
    } else {
      setCcSelectedClientIds([]);
      setCcClientId('');
      setCcCode('');
      setCcName('');
      setCcManager('');
      setCcAddress('');
      setCcLat(25.6866);
      setCcLng(-100.3161);
    }
  }, [editingCostCenter]);

  useEffect(() => {
    if (editingDestination) {
      setDestName(editingDestination.name);
      const initialClientIds = editingDestination.clientIds && editingDestination.clientIds.length > 0
        ? editingDestination.clientIds
        : (editingDestination.clientId ? [editingDestination.clientId] : []);
      setDestSelectedClientIds(initialClientIds);
      setDestClientId(editingDestination.clientId || (initialClientIds[0] || ''));
      setDestAddress(editingDestination.address || '');
      setDestLat(editingDestination.lat ?? 25.6866);
      setDestLng(editingDestination.lng ?? -100.3161);
      setDestRouteNotes(editingDestination.routeNotes || '');
      setDestEstimatedKm(editingDestination.estimatedKm ?? 45);
      setDestEstimatedMins(editingDestination.estimatedMins ?? 40);
    } else {
      setDestName('');
      setDestSelectedClientIds([]);
      setDestClientId('');
      setDestAddress('');
      setDestLat(25.6866);
      setDestLng(-100.3161);
      setDestRouteNotes('');
      setDestEstimatedKm(45);
      setDestEstimatedMins(40);
    }
  }, [editingDestination]);

  const handleCreateDestination = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destName.trim() || !destAddress.trim()) {
      await window.showAppAlert('El nombre del destino/ruta y la dirección son obligatorios.', 'Campos Requeridos');
      return;
    }

    const matchedClients = clients.filter(c => destSelectedClientIds.includes(c.id));
    const primaryClientId = destSelectedClientIds.length > 0 ? destSelectedClientIds[0] : undefined;
    const clientNameLabel = destSelectedClientIds.length > 0 
      ? matchedClients.map(c => c.name).join(' / ') 
      : undefined;

    if (editingDestination) {
      const updated: Destination = {
        ...editingDestination,
        name: destName.trim(),
        clientId: primaryClientId,
        clientName: clientNameLabel,
        clientIds: destSelectedClientIds,
        address: destAddress.trim(),
        destinationAddress: destAddress.trim(),
        lat: Number(destLat) || 25.6866,
        lng: Number(destLng) || -100.3161,
        routeNotes: destRouteNotes.trim(),
        estimatedKm: Number(destEstimatedKm) || 0,
        estimatedMins: Number(destEstimatedMins) || 0,
      };

      try {
        if (onUpdateDestination) {
          onUpdateDestination(updated);
        } else {
          await dataService.saveDestination(updated);
        }
        setEditingDestination(null);
        setDestSelectedClientIds([]);
        onLogActivity('ACTUALIZACION_DESTINO', `Destino/Ruta actualizado: ${updated.name}`);
        await window.showAppAlert('Destino o ruta actualizado exitosamente.', 'Destino Actualizado');
      } catch (err) {
        await window.showAppAlert('Error al actualizar destino: ' + err, 'Error de Operación');
      }
    } else {
      const newDest: Destination = {
        id: `DEST-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        name: destName.trim(),
        clientId: primaryClientId,
        clientName: clientNameLabel,
        clientIds: destSelectedClientIds,
        address: destAddress.trim(),
        destinationAddress: destAddress.trim(),
        lat: Number(destLat) || 25.6866,
        lng: Number(destLng) || -100.3161,
        routeNotes: destRouteNotes.trim(),
        estimatedKm: Number(destEstimatedKm) || 0,
        estimatedMins: Number(destEstimatedMins) || 0,
      };

      try {
        if (onAddDestination) {
          onAddDestination(newDest);
        } else {
          await dataService.saveDestination(newDest);
        }
        setDestName('');
        setDestClientId('');
        setDestSelectedClientIds([]);
        setDestAddress('');
        setDestLat(25.6866);
        setDestLng(-100.3161);
        setDestRouteNotes('');
        setDestEstimatedKm(45);
        setDestEstimatedMins(40);
        onLogActivity('CREACION_DESTINO', `Nuevo destino/ruta registrado: ${newDest.name}`);
        await window.showAppAlert('Destino o ruta registrado correctamente.', 'Destino Creado');
      } catch (err) {
        await window.showAppAlert('Error al registrar destino: ' + err, 'Error de Operación');
      }
    }
  };

  const handleDeleteDestinationClick = async (id: string) => {
    const target = destinations.find(d => d.id === id);
    if (!target) return;
    const confirm = await window.showAppConfirm(`¿Está seguro de eliminar el destino "${target.name}"?`, 'Eliminar Destino');
    if (confirm) {
      try {
        if (onDeleteDestination) {
          onDeleteDestination(id);
        } else {
          await dataService.deleteDestination(id);
        }
        onLogActivity('ELIMINACION_DESTINO', `Se eliminó el destino de entrega: ${target.name}`);
        await window.showAppAlert('Ubicación de destino eliminada.', 'Destino Eliminado');
      } catch (err) {
        await window.showAppAlert('Error al eliminar destino: ' + err, 'Error de Operación');
      }
    }
  };

  const filteredDestinations = useMemo(() => {
    let list = destinations;
    if (destClientFilter === 'GENERAL') {
      list = list.filter(d => !d.clientId && (!d.clientIds || d.clientIds.length === 0) && !d.clientName);
    } else if (destClientFilter !== 'ALL') {
      list = list.filter(d => 
        d.clientId === destClientFilter || 
        (d.clientIds && d.clientIds.includes(destClientFilter)) ||
        (d.clientName && clients.find(c => c.id === destClientFilter)?.name.toLowerCase() === d.clientName.toLowerCase())
      );
    }
    if (destSearchTerm.trim()) {
      const q = destSearchTerm.toLowerCase();
      list = list.filter(d => 
        d.name.toLowerCase().includes(q) ||
        d.address.toLowerCase().includes(q) ||
        (d.routeNotes && d.routeNotes.toLowerCase().includes(q)) ||
        (d.clientName && d.clientName.toLowerCase().includes(q))
      );
    }
    return list;
  }, [destinations, destClientFilter, destSearchTerm, clients]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uName || !uEmail) return;

    const primaryClientCode = uAssignedClientIds.length > 0 
      ? (clients.find(c => c.id === uAssignedClientIds[0] || c.code === uAssignedClientIds[0])?.code || uAssignedClientIds[0]) 
      : (isClientRole(uRole) ? uClientCode : undefined);

    if (editingUser) {
      // Update User mode
      const updatedUser: UserProfile = {
        ...editingUser,
        email: uEmail.trim().toLowerCase(),
        name: uName.trim(),
        role: uRole,
        password: uPassword,
        clientCode: primaryClientCode,
        assignedClientIds: uAssignedClientIds,
        assignedCostCenterIds: uAssignedCostCenterIds,
        permissions: {
          ...uPerms,
          canManageUsers: isAdminRole(uRole) ? true : uPerms.canManageUsers
        }
      };

      try {
        await dataService.saveUser(updatedUser);
        setUsers(prev => prev.map(u => u.uid === updatedUser.uid ? updatedUser : u));
        onLogActivity('ACTUALIZACION_USUARIO', `Usuario actualizado: ${updatedUser.name} (${updatedUser.email}) con rol ${updatedUser.role}`);
        setEditingUser(null);
        await window.showAppAlert('Usuario actualizado exitosamente.', 'Usuario Actualizado');
      } catch (err) {
        await window.showAppAlert('Error al actualizar usuario: ' + err, 'Error de Operación');
      }
    } else {
      // Create User mode
      const newUser: UserProfile = {
        uid: `u-${Math.random().toString(36).substr(2, 9)}`,
        email: uEmail.trim().toLowerCase(),
        name: uName.trim(),
        role: uRole,
        password: uPassword,
        clientCode: primaryClientCode,
        assignedClientIds: uAssignedClientIds,
        assignedCostCenterIds: uAssignedCostCenterIds,
        permissions: {
          ...uPerms,
          canManageUsers: isAdminRole(uRole) ? true : uPerms.canManageUsers
        }
      };

      try {
        await dataService.saveUser(newUser);
        setUsers(prev => [...prev, newUser]);
        
        // Log Action
        onLogActivity('CREACION_USUARIO', `Usuario registrado: ${newUser.name} (${newUser.email}) con rol ${newUser.role}`);
        
        // Reset User Form
        setUName('');
        setUEmail('');
        setURole('OPERADOR');
        setUClientCode('');
        setUAssignedClientIds([]);
        setUAssignedCostCenterIds([]);
        setUPassword('demo1234');
        setUPerms({
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: false,
          canBilling: true,
          menuAccess: {},
        });
        await window.showAppAlert('Usuario creado y registrado exitosamente en Firebase Auth/Firestore.', 'Usuario Registrado');
      } catch (err) {
        await window.showAppAlert('Error al registrar usuario: ' + err, 'Error de Operación');
      }
    }
  };

  const handleDeleteUser = async (uid: string) => {
    const userToDelete = users.find(u => u.uid === uid || u.id === uid);
    if (!userToDelete) return;
    const isConfirmed = await window.showAppConfirm(`¿Está seguro de que desea eliminar al usuario ${userToDelete.name}?`, 'Eliminar Usuario');
    if (isConfirmed) {
      try {
        await dataService.deleteUser(uid, userToDelete.email);
        setUsers(prev => prev.filter(u => u.uid !== uid && u.id !== uid && u.email.toLowerCase() !== userToDelete.email.toLowerCase()));
        onLogActivity('ELIMINACION_USUARIO', `Usuario eliminado: ${userToDelete.name} (${userToDelete.email})`);
        await window.showAppAlert('Usuario eliminado correctamente.', 'Usuario Eliminado');
      } catch (err) {
        await window.showAppAlert('Error al eliminar usuario: ' + err, 'Error de Operación');
      }
    }
  };

  const handleCreateClientAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cCode || !cName || !cRfc) return;

    if (editingClient) {
      const updatedClient: Client = {
        ...editingClient,
        code: cCode.trim().toUpperCase(),
        name: cName.trim(),
        rfc: cRfc.trim().toUpperCase(),
        contactEmail: cEmail.trim(),
        contactPhone: cPhone.trim(),
        address: cAddress.trim(),
      };

      try {
        if (onUpdateClient) {
          onUpdateClient(updatedClient);
        }
        setEditingClient(null);
        await window.showAppAlert('Cliente actualizado exitosamente.', 'Cliente Actualizado');
      } catch (err) {
        await window.showAppAlert('Error al actualizar cliente: ' + err, 'Error de Operación');
      }
    } else {
      const clientId = `c-${Math.random().toString(36).substr(2, 9)}`;
      const newClient: Client = {
        id: clientId,
        code: cCode.trim().toUpperCase(),
        name: cName.trim(),
        rfc: cRfc.trim().toUpperCase(),
        contactEmail: cEmail.trim(),
        contactPhone: cPhone.trim(),
        address: cAddress.trim(),
        lat: 25.6866 + (Math.random() - 0.5) * 0.1,
        lng: -100.3161 + (Math.random() - 0.5) * 0.1
      };

      try {
        // Add client
        onAddClient(newClient);

        let logDetails = `Cliente creado: ${newClient.name} (${newClient.code})`;

        // Cost Center assignment/creation logic
        if (assignCcOption === 'existing' && selectedCcId) {
          const foundCc = costCenters.find(cc => cc.id === selectedCcId);
          if (foundCc) {
            const updatedCc: CostCenter = {
              ...foundCc,
              clientId: clientId
            };
            onAddCostCenter(updatedCc);
            logDetails += `. Asignado al centro de costos existente: ${foundCc.name}`;
          }
        } else if (assignCcOption === 'new' && newCcCode && newCcName) {
          const newCC: CostCenter = {
            id: `cc-${Math.random().toString(36).substr(2, 9)}`,
            clientId: clientId,
            code: newCcCode.trim().toUpperCase(),
            name: newCcName.trim(),
            manager: newCcManager.trim() || 'Ing. de Enlace',
            address: newCcAddress.trim() || cAddress.trim(),
            lat: 25.6866 + (Math.random() - 0.5) * 0.1,
            lng: -100.3161 + (Math.random() - 0.5) * 0.1
          };
          onAddCostCenter(newCC);
          logDetails += `. Creado y vinculado nuevo centro de costos: ${newCC.name} (${newCC.code})`;
        }

        onLogActivity('CREACION_CLIENTE', logDetails);

        // Reset
        setCCode('');
        setCName('');
        setCRfc('');
        setCEmail('');
        setCPhone('');
        setCAddress('');
        setAssignCcOption('none');
        setSelectedCcId('');
        setNewCcCode('');
        setNewCcName('');
        setNewCcManager('');
        setNewCcAddress('');

        await window.showAppAlert('Cliente registrado y asignado exitosamente.', 'Cliente Registrado');
      } catch (err) {
        await window.showAppAlert('Error registrando cliente: ' + err, 'Error de Operación');
      }
    }
  };

  const handleDeleteClient = async (id: string) => {
    const clientToDelete = clients.find(c => c.id === id);
    if (!clientToDelete) return;
    const isConfirmed = await window.showAppConfirm(`¿Está seguro de que desea eliminar al cliente ${clientToDelete.name}?`, 'Eliminar Cliente');
    if (isConfirmed) {
      try {
        if (onDeleteClient) {
          onDeleteClient(id);
          await window.showAppAlert('Cliente eliminado correctamente.', 'Cliente Eliminado');
        }
      } catch (err) {
        await window.showAppAlert('Error al eliminar cliente: ' + err, 'Error de Operación');
      }
    }
  };

  const handleCreateCostCenterAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ccCode.trim() || !ccName.trim()) {
      await window.showAppAlert('Por favor ingrese el código y nombre del centro de costos.', 'Campos Requeridos');
      return;
    }

    const primaryClientId = ccSelectedClientIds.length > 0 ? ccSelectedClientIds[0] : (ccClientId || undefined);
    const finalClientIds = ccSelectedClientIds.length > 0 ? ccSelectedClientIds : (primaryClientId ? [primaryClientId] : []);

    const parsedLat = typeof ccLat === 'number' ? ccLat : (parseFloat(String(ccLat)) || 25.6866);
    const parsedLng = typeof ccLng === 'number' ? ccLng : (parseFloat(String(ccLng)) || -100.3161);

    if (editingCostCenter) {
      const updatedCC: CostCenter = {
        ...editingCostCenter,
        clientId: primaryClientId,
        clientIds: finalClientIds,
        code: ccCode.trim().toUpperCase(),
        name: ccName.trim(),
        manager: ccManager.trim(),
        address: ccAddress.trim(),
        lat: parsedLat,
        lng: parsedLng,
      };

      try {
        if (onUpdateCostCenter) {
          onUpdateCostCenter(updatedCC);
        }
        setEditingCostCenter(null);
        if (finalClientIds.length > 0) {
          await window.showAppAlert(`Centro de costos actualizado exitosamente vinculado a ${finalClientIds.length} cliente(s).`, 'Sede Actualizada');
        } else {
          await window.showAppAlert('Centro de costos actualizado exitosamente para uso exclusivo interno (sin clientes mandantes).', 'Sede Actualizada');
        }
      } catch (err) {
        await window.showAppAlert('Error al actualizar centro de costos: ' + err, 'Error de Operación');
      }
    } else {
      const newCC: CostCenter = {
        id: `cc-${Math.random().toString(36).substr(2, 9)}`,
        clientId: primaryClientId,
        clientIds: finalClientIds,
        code: ccCode.trim().toUpperCase(),
        name: ccName.trim(),
        manager: ccManager.trim(),
        address: ccAddress.trim(),
        lat: parsedLat,
        lng: parsedLng,
      };

      try {
        onAddCostCenter(newCC);
        const logMsg = finalClientIds.length > 0
          ? `Centro de costos creado: ${newCC.name} (${newCC.code}) vinculado a ${finalClientIds.length} cliente(s)`
          : `Centro de costos de uso interno creado: ${newCC.name} (${newCC.code}) sin cliente mandante`;
        onLogActivity('CREACION_CENTRO_COSTOS', logMsg);

        setCcSelectedClientIds([]);
        setCcClientId('');
        setCcCode('');
        setCcName('');
        setCcManager('');
        setCcAddress('');
        setCcLat(25.6866);
        setCcLng(-100.3161);
        if (finalClientIds.length > 0) {
          await window.showAppAlert(`Centro de costos registrado exitosamente vinculado a ${finalClientIds.length} cliente(s).`, 'Sede Registrada');
        } else {
          await window.showAppAlert('Centro de costos registrado exitosamente para uso exclusivo interno (sin cliente mandante). Al seleccionarlo en el sistema, solo mostrará la carpeta de INTERNO.', 'Sede Interna Registrada');
        }
      } catch (err) {
        await window.showAppAlert('Error al registrar centro de costos: ' + err, 'Error de Operación');
      }
    }
  };

  const handleDeleteCostCenter = async (id: string) => {
    const ccToDelete = costCenters.find(cc => cc.id === id);
    if (!ccToDelete) return;
    const isConfirmed = await window.showAppConfirm(`¿Está seguro de que desea eliminar el centro de costos ${ccToDelete.name}?`, 'Eliminar Sede');
    if (isConfirmed) {
      try {
        if (onDeleteCostCenter) {
          onDeleteCostCenter(id);
          await window.showAppAlert('Centro de costos eliminado correctamente.', 'Sede Eliminada');
        }
      } catch (err) {
        await window.showAppAlert('Error al eliminar centro de costos: ' + err, 'Error de Operación');
      }
    }
  };

  const togglePermission = (key: keyof typeof uPerms) => {
    if (key === 'menuAccess') return;
    setUPerms(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleMenuAccess = (menuKey: string) => {
    setUPerms(prev => {
      const currentMenuAccess = prev.menuAccess || {};
      return {
        ...prev,
        menuAccess: {
          ...currentMenuAccess,
          [menuKey]: !currentMenuAccess[menuKey]
        }
      };
    });
  };

  const handleSaveSupportArea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!areaName || !areaAgent || !areaPhone || !areaDesc) {
      await window.showAppAlert('Todos los campos son requeridos para guardar un área de soporte.', 'Validación');
      return;
    }

    if (!/^\d{10}$/.test(areaPhone.trim())) {
      await window.showAppAlert('El número telefónico de WhatsApp debe constar de exactamente 10 dígitos numéricos (ej. 8712397902).', 'Error de Formato');
      return;
    }

    if (!areaAllClients && areaSelectedClientIds.length === 0) {
      await window.showAppAlert('Debe seleccionar al menos un cliente o activar la opción de atención general para todos los clientes.', 'Validación de Cliente');
      return;
    }

    if (!areaAllCostCenters && areaCostCenterIds.length === 0) {
      await window.showAppAlert('Debe seleccionar al menos un centro de costos o activar la opción de cobertura global para todas las sedes.', 'Validación de Sede');
      return;
    }

    const matchedClients = clients.filter(c => areaSelectedClientIds.includes(c.id));
    const primaryClientId = areaAllClients ? undefined : (areaSelectedClientIds[0] || undefined);
    const clientNameLabel = areaAllClients 
      ? 'Todos los Clientes' 
      : (matchedClients.length > 0 ? matchedClients.map(c => c.name).join(' / ') : undefined);

    let updatedAreas: SupportArea[] = [];
    if (editingAreaId) {
      // Update existing
      updatedAreas = supportAreas.map(area => {
        if (area.id === editingAreaId) {
          return {
            ...area,
            name: areaName.trim(),
            agent: areaAgent.trim(),
            phone: areaPhone.trim(),
            desc: areaDesc.trim(),
            color: areaColor,
            clientId: primaryClientId,
            clientName: clientNameLabel,
            clientIds: areaAllClients ? [] : areaSelectedClientIds,
            allClients: areaAllClients,
            costCenterIds: areaAllCostCenters ? [] : areaCostCenterIds,
            allCostCenters: areaAllCostCenters
          };
        }
        return area;
      });
      onLogActivity('EDITAR_SOPORTE_WHATSAPP', `Se editó el área de soporte "${areaName}" (Encargado: ${areaAgent})`);
      await window.showAppAlert(`Área "${areaName}" actualizada correctamente.`, 'Éxito');
    } else {
      // Create new
      const safeId = areaName.toLowerCase().replace(/[^a-z0-9]/g, '-') + '-' + Math.random().toString(36).substring(2, 6);
      const newArea: SupportArea = {
        id: safeId,
        name: areaName.trim(),
        agent: areaAgent.trim(),
        phone: areaPhone.trim(),
        desc: areaDesc.trim(),
        color: areaColor,
        clientId: primaryClientId,
        clientName: clientNameLabel,
        clientIds: areaAllClients ? [] : areaSelectedClientIds,
        allClients: areaAllClients,
        costCenterIds: areaAllCostCenters ? [] : areaCostCenterIds,
        allCostCenters: areaAllCostCenters
      };
      updatedAreas = [...supportAreas, newArea];
      onLogActivity('AGREGAR_SOPORTE_WHATSAPP', `Se agregó una nueva área de soporte "${areaName}" (Encargado: ${areaAgent})`);
      await window.showAppAlert(`Nueva área "${areaName}" creada correctamente.`, 'Éxito');
    }

    setSupportAreas(updatedAreas);
    localStorage.setItem('snfsic_whatsapp_support_areas', JSON.stringify(updatedAreas));

    // Reset form states
    setEditingAreaId(null);
    setAreaName('');
    setAreaAgent('');
    setAreaPhone('');
    setAreaDesc('');
    setAreaColor('emerald');
    setAreaSelectedClientIds([]);
    setAreaAllClients(true);
    setAreaCostCenterIds([]);
    setAreaAllCostCenters(true);
  };

  const handleEditSupportArea = (area: SupportArea) => {
    setEditingAreaId(area.id);
    setAreaName(area.name);
    setAreaAgent(area.agent);
    setAreaPhone(area.phone);
    setAreaDesc(area.desc);
    setAreaColor(area.color || 'emerald');
    const initialClientIds = area.clientIds && area.clientIds.length > 0
      ? area.clientIds
      : (area.clientId ? [area.clientId] : []);
    setAreaSelectedClientIds(initialClientIds);
    setAreaAllClients(area.allClients ?? (initialClientIds.length === 0));
    setAreaCostCenterIds(area.costCenterIds || []);
    setAreaAllCostCenters(area.allCostCenters ?? (!area.costCenterIds || area.costCenterIds.length === 0));
  };

  const handleDeleteSupportArea = async (id: string) => {
    const areaToDelete = supportAreas.find(a => a.id === id);
    if (!areaToDelete) return;

    if (supportAreas.length <= 1) {
      await window.showAppAlert('No puedes eliminar todas las áreas de soporte. Debe existir al menos un canal disponible.', 'No permitido');
      return;
    }

    const confirm = await window.showAppConfirm(`¿Seguro que deseas eliminar el canal de soporte para "${areaToDelete.name}"?`, 'Eliminar canal');
    if (confirm) {
      const updatedAreas = supportAreas.filter(a => a.id !== id);
      setSupportAreas(updatedAreas);
      localStorage.setItem('snfsic_whatsapp_support_areas', JSON.stringify(updatedAreas));
      onLogActivity('ELIMINAR_SOPORTE_WHATSAPP', `Se eliminó el canal de soporte "${areaToDelete.name}"`);
      await window.showAppAlert(`Canal "${areaToDelete.name}" eliminado de forma exitosa.`, 'Eliminado');

      if (editingAreaId === id) {
        setEditingAreaId(null);
        setAreaName('');
        setAreaAgent('');
        setAreaPhone('');
        setAreaDesc('');
        setAreaColor('emerald');
        setAreaSelectedClientIds([]);
        setAreaAllClients(true);
        setAreaCostCenterIds([]);
        setAreaAllCostCenters(true);
      }
    }
  };

  const handleRestoreDefaultAreas = async () => {
    const confirm = await window.showAppConfirm('¿Seguro que deseas restaurar las áreas de soporte de WhatsApp por defecto (Facturación y Almacén)? Esta acción reemplazará la configuración actual.', 'Restaurar valores');
    if (confirm) {
      const defaultAreas: SupportArea[] = [
        {
          id: 'facturacion',
          name: 'Departamento de Facturación',
          agent: 'Lic. Ana Karen Silva',
          phone: '8712397902',
          desc: 'Dudas de cobranza, prefacturas, complementos de pago y refacturaciones.',
          color: 'emerald',
          allClients: true,
          clientIds: [],
          allCostCenters: true,
          costCenterIds: []
        },
        {
          id: 'almacen',
          name: 'Control de Almacén y Logística',
          agent: 'Ing. Mateo Rodríguez',
          phone: '4622996955',
          desc: 'Estado físico de piezas, recepción de material, granallado y despacho.',
          color: 'blue',
          allClients: true,
          clientIds: [],
          allCostCenters: true,
          costCenterIds: []
        }
      ];
      setSupportAreas(defaultAreas);
      localStorage.setItem('snfsic_whatsapp_support_areas', JSON.stringify(defaultAreas));
      onLogActivity('RESTAURAR_SOPORTE_WHATSAPP', 'Se restauraron los canales de soporte de WhatsApp por defecto');
      await window.showAppAlert('Canales de soporte restaurados por defecto correctamente.', 'Restaurado');

      setEditingAreaId(null);
      setAreaName('');
      setAreaAgent('');
      setAreaPhone('');
      setAreaDesc('');
      setAreaColor('emerald');
      setAreaSelectedClientIds([]);
      setAreaAllClients(true);
      setAreaCostCenterIds([]);
      setAreaAllCostCenters(true);
    }
  };

  return (
    <div className="space-y-4 text-slate-800">
      {/* Header Banner */}
      <div className="bg-brand-dark border border-slate-800 p-4 rounded-lg flex items-center justify-between">
        <div>
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-white">Portal de Administración Gerencial</h2>
          <p className="text-[11px] text-slate-300">Panel de control de usuarios, permisos, creación de clientes, centros de costos y bitácora de actividad en tiempo real.</p>
        </div>
        <div className="bg-brand-red/15 border border-brand-red/30 text-brand-red px-2.5 py-1 rounded-sm text-[10px] font-mono font-bold uppercase shrink-0">
          Rol: Corporativo SNF
        </div>
      </div>

      {/* Admin Tab Navigation */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => { setActiveSubTab('users'); setEditingUser(null); }}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'users' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users className="h-4 w-4" /> Usuarios y Permisos
        </button>
        <button
          onClick={() => { setActiveSubTab('clients'); setEditingClient(null); }}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'clients' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Briefcase className="h-4 w-4" /> Crear Clientes y Asignar
        </button>
        <button
          onClick={() => { setActiveSubTab('costCenters'); setEditingCostCenter(null); }}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'costCenters' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <MapPin className="h-4 w-4" /> Crear Centros de Costos
        </button>
        <button
          onClick={() => { setActiveSubTab('destinos'); setEditingDestination(null); }}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'destinos' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Navigation className="h-4 w-4" /> Destinos y Rutas
        </button>
        <button
          onClick={() => setActiveSubTab('activity')}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'activity' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Activity className="h-4 w-4" /> Bitácora de Actividad
        </button>
        <button
          onClick={() => setActiveSubTab('database')}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'database' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Lock className="h-4 w-4" /> Limpieza de Base de Datos
        </button>
        <button
          onClick={() => setActiveSubTab('whatsapp')}
          className={`pb-2.5 text-xs font-bold border-b-2 px-4 transition-all flex items-center gap-1.5 cursor-pointer ${
            activeSubTab === 'whatsapp' 
              ? 'border-brand-red text-brand-red' 
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <MessageCircle className="h-4 w-4 text-emerald-500" /> Soporte WhatsApp
        </button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center">
          <p className="text-xs text-slate-500">Cargando base de datos administrativa...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          
          {/* USERS & PERMISSIONS SUBTAB */}
          {activeSubTab === 'users' && (
            <>
              {/* User list */}
              <div className="xl:col-span-2 space-y-3">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Directorio de Usuarios</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {users.map((u, idx) => (
                    <div key={u.uid ? `${u.uid}-${idx}` : `user-${idx}`} className="bg-white p-3.5 rounded-lg border border-slate-200/80 shadow-tiny flex flex-col justify-between space-y-3 hover:border-slate-300 transition-all">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                            isAdminRole(u.role) 
                              ? 'bg-red-50 text-brand-red border border-red-100' 
                              : isClientRole(u.role) 
                                ? 'bg-blue-50 text-blue-700 border border-blue-100' 
                                : 'bg-amber-50 text-amber-700 border border-amber-100'
                          }`}>
                            {u.role}
                          </span>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => setEditingUser(u)}
                              className="text-[10px] text-brand-red hover:underline font-bold cursor-pointer"
                            >
                              Editar
                            </button>
                            <span className="text-slate-300">|</span>
                            <button 
                              onClick={() => handleDeleteUser(u.uid)}
                              className="text-[10px] text-slate-450 hover:text-red-600 font-medium cursor-pointer"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                        <h4 className="text-xs font-display font-bold text-slate-900 mt-1.5">{u.name}</h4>
                        <p className="text-[11px] text-slate-500">{u.email}</p>
                        
                        {/* Clientes Asignados Badge */}
                        {((u.assignedClientIds && u.assignedClientIds.length > 0) || u.clientCode) && (
                          <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                            <span className="text-[9px] font-bold text-slate-500">Clientes:</span>
                            {u.assignedClientIds && u.assignedClientIds.length > 0 ? (
                              u.assignedClientIds.map((cid, i) => {
                                const cObj = clients.find(c => c.id === cid || c.code === cid);
                                return (
                                  <span key={i} className="text-[9px] bg-blue-50 text-blue-700 font-bold px-1.5 py-0.2 rounded-xs border border-blue-100">
                                    {cObj ? cObj.code : cid}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-[9px] bg-slate-50 text-slate-600 font-bold px-1.5 py-0.2 rounded-xs border border-slate-200/50">
                                {u.clientCode}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Centros de Costos Asignados Badge */}
                        {u.role === 'ADMINISTRADOR' ? (
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                              🌐 Acceso Global (Todas las Sedes)
                            </span>
                          </div>
                        ) : u.assignedCostCenterIds && u.assignedCostCenterIds.length > 0 ? (
                          <div className="mt-1 flex flex-wrap gap-1 items-center">
                            <span className="text-[9px] font-bold text-slate-500">Centros Costo:</span>
                            {u.assignedCostCenterIds.map((ccid, i) => {
                              const ccObj = costCenters.find(cc => cc.id === ccid || cc.code === ccid || cc.name === ccid);
                              return (
                                <span key={i} className="text-[9px] bg-red-50 text-red-700 font-bold px-1.5 py-0.2 rounded-xs border border-red-100">
                                  {ccObj ? `${ccObj.name} (${ccObj.code})` : ccid}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                              🔒 Sin Sedes Asignadas (Restringido)
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-2.5">
                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Permisos de Módulo</span>
                        <div className="flex flex-wrap gap-1">
                          {u.permissions.canCreateFolios && <span className="text-[8px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.2 rounded-xs">Crear Folios</span>}
                          {u.permissions.canUpdateStages && <span className="text-[8px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.2 rounded-xs">Etapas</span>}
                          {u.permissions.canViewDashboard && <span className="text-[8px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.2 rounded-xs">Dashboard</span>}
                          {u.permissions.canManageUsers && <span className="text-[8px] font-bold text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.2 rounded-xs">Admin</span>}
                          {u.permissions.canBilling && <span className="text-[8px] font-bold text-teal-700 bg-teal-50 border border-teal-100 px-1.5 py-0.2 rounded-xs">Prefacturas</span>}
                          {!u.permissions.canCreateFolios && !u.permissions.canUpdateStages && !u.permissions.canViewDashboard && !u.permissions.canManageUsers && !u.permissions.canBilling && (
                            <span className="text-[8px] font-bold text-slate-400 bg-slate-50 border border-slate-150 px-1.5 py-0.2 rounded-xs">Lectura Limitada</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User create form */}
              <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny h-fit space-y-3.5">
                <div className="flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <UserPlus className="h-4.5 w-4.5 text-brand-red" />
                  <h4 className="text-xs font-bold text-slate-900 uppercase">
                    {editingUser ? `Editar Usuario: ${editingUser.name}` : 'Crear Nuevo Usuario'}
                  </h4>
                </div>

                <form onSubmit={handleCreateUser} className="space-y-3">
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre Completo</label>
                    <input
                      type="text"
                      required
                      placeholder="Ing. Juan Pérez"
                      value={uName}
                      onChange={(e) => setUName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Email (Acceso)</label>
                    <input
                      type="email"
                      required
                      placeholder="juan.perez@grupoisn.com.mx"
                      value={uEmail}
                      onChange={(e) => setUEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contraseña de Acceso</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={uPassword}
                      onChange={(e) => setUPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rol del Sistema</label>
                    <select
                      value={uRole}
                      onChange={(e) => {
                        const r = e.target.value as UserRole;
                        setURole(r);
                        if (isClientRole(r)) {
                          setUPerms({
                            canCreateFolios: false,
                            canUpdateStages: false,
                            canViewDashboard: false,
                            canManageUsers: false,
                            canBilling: false,
                            menuAccess: {
                              welcome: true,
                              client_dashboard: true,
                              client_catalog: true,
                              client_cargas_envios: true,
                              client_trazabilidad: true,
                              client_facturas_cobros: r === 'FACTURACION',
                              client_historial: true,
                              client_reprocesos: true,
                              client_kpis: true,
                              client_alertas: true,
                              client_chat_soporte: true,
                            }
                          });
                        } else {
                          setUPerms({
                            canCreateFolios: r === 'ADMINISTRADOR' || r === 'GERENTE' || r === 'COORDINADOR',
                            canUpdateStages: r !== 'CHOFER',
                            canViewDashboard: r === 'ADMINISTRADOR' || r === 'GERENTE',
                            canManageUsers: r === 'ADMINISTRADOR',
                            canBilling: r === 'ADMINISTRADOR' || r === 'GERENTE' || r === 'COORDINADOR',
                            menuAccess: {
                              welcome: true,
                              kpis: true,
                              dashboard: r === 'ADMINISTRADOR' || r === 'GERENTE',
                              calendar: true,
                              meetings: true,
                              admin_portal: r === 'ADMINISTRADOR',
                              clients: r === 'ADMINISTRADOR' || r === 'GERENTE',
                              internal_proyectos: true,
                              internal_seguridad: true,
                              internal_operaciones: true,
                              internal_rrhh: r === 'ADMINISTRADOR' || r === 'GERENTE',
                              internal_almacen: true,
                              internal_activos: true,
                              internal_mantenimiento: true,
                              internal_it_sistemas: r === 'ADMINISTRADOR',
                            }
                          });
                        }
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 focus:outline-hidden focus:border-brand-red"
                    >
                      <optgroup label="Roles de la Parte Interna (Staff)">
                        <option value="ADMINISTRADOR">ADMINISTRADOR</option>
                        <option value="GERENTE">GERENTE</option>
                        <option value="COORDINADOR">COORDINADOR</option>
                        <option value="SUPERVISOR">SUPERVISOR</option>
                        <option value="OPERADOR">OPERADOR</option>
                        <option value="CHOFER">CHOFER</option>
                      </optgroup>
                      <optgroup label="Roles de la Parte del Cliente (Socio)">
                        <option value="FACTURACION">FACTURACIÓN</option>
                        <option value="OPERACION">OPERACIÓN</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Multi-selection Clientes Asignados */}
                  <div className="space-y-1 pt-1 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-blue-600" />
                        <span>Clientes Asignados (Selección Múltiple)</span>
                      </label>
                      <div className="flex items-center gap-1.5 text-[9px]">
                        <button
                          type="button"
                          onClick={() => setUAssignedClientIds(clients.map(c => c.id))}
                          className="text-blue-600 hover:underline font-bold cursor-pointer"
                        >
                          Todos
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          onClick={() => setUAssignedClientIds([])}
                          className="text-slate-400 hover:underline cursor-pointer"
                        >
                          Ninguno
                        </button>
                      </div>
                    </div>
                    <p className="text-[9px] text-slate-400 leading-tight">
                      Seleccione los clientes a los que este usuario o chofer tendrá acceso ({uAssignedClientIds.length} seleccionados).
                    </p>
                    <div className="max-h-36 overflow-y-auto bg-slate-50 p-2 rounded-md border border-slate-200/80 space-y-1">
                      {clients.length > 0 ? (
                        clients.map(client => {
                          const isSelected = uAssignedClientIds.includes(client.id) || uAssignedClientIds.includes(client.code);
                          return (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() => toggleAssignedClient(client.id)}
                              className={`w-full flex items-center justify-between p-1.5 rounded-md text-xs transition-all cursor-pointer text-left ${
                                isSelected
                                  ? 'bg-blue-50/90 border border-blue-300 text-blue-900 font-bold shadow-2xs'
                                  : 'bg-white border border-slate-200/60 text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                {isSelected ? <CheckSquare className="h-3.5 w-3.5 text-blue-600 shrink-0 font-bold" /> : <Square className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                                <span className="truncate">{client.name}</span>
                              </div>
                              <span className="text-[9px] font-mono font-bold bg-slate-100 text-slate-600 px-1 py-0.2 rounded-xs shrink-0">{client.code}</span>
                            </button>
                          );
                        })
                      ) : (
                        <p className="text-[10px] text-slate-400 italic text-center py-1">No hay clientes registrados</p>
                      )}
                    </div>
                  </div>

                  {/* Multi-selection Centros de Costo Asignados */}
                  <div className="space-y-1 pt-1">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-red-600" />
                        <span>Centros de Costos Asignados (Selección Múltiple)</span>
                      </label>
                      <div className="flex items-center gap-1.5 text-[9px]">
                        <button
                          type="button"
                          onClick={() => setUAssignedCostCenterIds(costCenters.map(cc => cc.id))}
                          className="text-red-600 hover:underline font-bold cursor-pointer"
                        >
                          Todos
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          onClick={() => setUAssignedCostCenterIds([])}
                          className="text-slate-400 hover:underline cursor-pointer"
                        >
                          Ninguno
                        </button>
                      </div>
                    </div>
                    <p className="text-[9px] text-slate-400 leading-tight">
                      Restringe o expande las plantas / centros de costo visibles para el usuario ({uAssignedCostCenterIds.length} seleccionados).
                    </p>
                    {uAssignedCostCenterIds.length === 0 && !isAdminRole(uRole) && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded text-[9px] flex items-center gap-1 font-medium">
                        <span>⚠️ Sin centros de costos asignados: el usuario solo podrá ver los centros que le sean asignados aquí.</span>
                      </div>
                    )}
                    <div className="max-h-36 overflow-y-auto bg-slate-50 p-2 rounded-md border border-slate-200/80 space-y-1">
                      {costCenters.length > 0 ? (
                        costCenters.map(cc => {
                          const isSelected = uAssignedCostCenterIds.includes(cc.id) || uAssignedCostCenterIds.includes(cc.code) || uAssignedCostCenterIds.includes(cc.name);
                          return (
                            <button
                              key={cc.id}
                              type="button"
                              onClick={() => toggleAssignedCostCenter(cc.id)}
                              className={`w-full flex items-center justify-between p-1.5 rounded-md text-xs transition-all cursor-pointer text-left ${
                                isSelected
                                  ? 'bg-red-50/90 border border-red-300 text-red-900 font-bold shadow-2xs'
                                  : 'bg-white border border-slate-200/60 text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                {isSelected ? <CheckSquare className="h-3.5 w-3.5 text-red-600 shrink-0 font-bold" /> : <Square className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                                <span className="truncate">{cc.name}</span>
                              </div>
                              <span className="text-[9px] font-mono font-bold bg-slate-100 text-slate-600 px-1 py-0.2 rounded-xs shrink-0">{cc.code}</span>
                            </button>
                          );
                        })
                      ) : (
                        <p className="text-[10px] text-slate-400 italic text-center py-1">No hay centros de costo registrados</p>
                      )}
                    </div>
                  </div>

                  {isClientRole(uRole) && (
                    <div className="space-y-0.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vincular Código Cliente</label>
                      <select
                        required
                        value={uClientCode}
                        onChange={(e) => setUClientCode(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 focus:outline-hidden focus:border-brand-red"
                      >
                        <option value="">Seleccione el cliente...</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-3 pt-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Permisos de Módulos (Acciones)</label>
                      <div className="grid grid-cols-2 gap-1.5 bg-slate-50 p-2.5 rounded-md border border-slate-200/50">
                        <button
                          type="button"
                          onClick={() => togglePermission('canCreateFolios')}
                          className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                        >
                          {uPerms.canCreateFolios ? <CheckSquare className="h-4 w-4 text-brand-red font-bold" /> : <Square className="h-4 w-4 text-slate-400" />}
                          <span>Registrar folios</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePermission('canUpdateStages')}
                          className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                        >
                          {uPerms.canUpdateStages ? <CheckSquare className="h-4 w-4 text-brand-red font-bold" /> : <Square className="h-4 w-4 text-slate-400" />}
                          <span>Actualizar etapas</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePermission('canViewDashboard')}
                          className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                        >
                          {uPerms.canViewDashboard ? <CheckSquare className="h-4 w-4 text-brand-red font-bold" /> : <Square className="h-4 w-4 text-slate-400" />}
                          <span>Ver Dashboard Gerencial</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePermission('canBilling')}
                          className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                        >
                          {uPerms.canBilling ? <CheckSquare className="h-4 w-4 text-brand-red font-bold" /> : <Square className="h-4 w-4 text-slate-400" />}
                          <span>Autorizar prefacturas</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePermission('canManageUsers')}
                          className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer col-span-2 border-t border-slate-200/60 pt-1 mt-1 animate-none"
                        >
                          {uPerms.canManageUsers ? <CheckSquare className="h-4 w-4 text-brand-red font-bold" /> : <Square className="h-4 w-4 text-slate-400" />}
                          <span className="font-semibold text-brand-red">Administrar Usuarios y Portal</span>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Acceso Visual a Menús (Panel Lateral)</label>
                      <p className="text-[9px] text-slate-400 leading-tight">Marque los menús del panel lateral que este usuario podrá visualizar y navegar.</p>
                      
                      <div className="space-y-3 max-h-[300px] overflow-y-auto bg-slate-50 p-2.5 rounded-md border border-slate-200/50">
                        {/* Category General */}
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block border-b border-slate-200 pb-0.5 mb-1.5">Menús Generales</span>
                          <div className="space-y-1">
                            {ALL_MENU_OPTIONS.filter(m => m.category === 'General').map(m => {
                              const isChecked = !!uPerms.menuAccess?.[m.key];
                              return (
                                <button
                                  key={m.key}
                                  type="button"
                                  onClick={() => toggleMenuAccess(m.key)}
                                  className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                                >
                                  {isChecked ? <CheckSquare className="h-3.5 w-3.5 text-blue-600 font-bold" /> : <Square className="h-3.5 w-3.5 text-slate-400" />}
                                  <span>{m.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Category Socio Cliente */}
                        <div className="mt-2.5">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block border-b border-slate-200 pb-0.5 mb-1.5">Mapeo del Socio / Cliente (11 submenús)</span>
                          <div className="space-y-1">
                            {ALL_MENU_OPTIONS.filter(m => m.category === 'Socio Cliente').map(m => {
                              const isChecked = !!uPerms.menuAccess?.[m.key];
                              return (
                                <button
                                  key={m.key}
                                  type="button"
                                  onClick={() => toggleMenuAccess(m.key)}
                                  className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                                >
                                  {isChecked ? <CheckSquare className="h-3.5 w-3.5 text-emerald-600 font-bold" /> : <Square className="h-3.5 w-3.5 text-slate-400" />}
                                  <span>{m.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Category Interno Staff */}
                        <div className="mt-2.5">
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block border-b border-slate-200 pb-0.5 mb-1.5">Mapeo Interno / Staff (8 submenús)</span>
                          <div className="space-y-1">
                            {ALL_MENU_OPTIONS.filter(m => m.category === 'Interno Staff').map(m => {
                              const isChecked = !!uPerms.menuAccess?.[m.key];
                              return (
                                <button
                                  key={m.key}
                                  type="button"
                                  onClick={() => toggleMenuAccess(m.key)}
                                  className="flex items-center gap-2 text-xs text-slate-700 w-full text-left cursor-pointer animate-none"
                                >
                                  {isChecked ? <CheckSquare className="h-3.5 w-3.5 text-purple-600 font-bold" /> : <Square className="h-3.5 w-3.5 text-slate-400" />}
                                  <span>{m.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="submit"
                      className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-tiny"
                    >
                      {editingUser ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      <span>{editingUser ? 'Guardar Cambios' : 'Registrar Usuario'}</span>
                    </button>
                    {editingUser && (
                      <button
                        type="button"
                        onClick={() => setEditingUser(null)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </>
          )}
          {/* CREATE CLIENTS & ASIGN SUBTAB */}
          {activeSubTab === 'clients' && (
            <>
              {/* Clients Directory Summary */}
              <div className="xl:col-span-2 space-y-3">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Directorio de Clientes</h3>
                <div className="space-y-2">
                  {clients.map(client => {
                    const clientCcs = costCenters.filter(cc => cc.clientId === client.id);
                    return (
                      <div key={client.id} className="bg-white p-3 rounded-lg border border-slate-200/80 shadow-tiny flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] bg-red-50 text-brand-red font-mono font-bold px-1.5 py-0.2 rounded-xs">
                              {client.code}
                            </span>
                            <h4 className="text-xs font-display font-bold text-slate-900">{client.name}</h4>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">RFC: {client.rfc} | {client.contactEmail}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[9px] bg-slate-50 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-sm font-semibold font-mono">
                            {clientCcs.length} Centros de Costos
                          </span>
                          <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
                            <button 
                              onClick={() => setEditingClient(client)}
                              className="text-[10px] text-brand-red hover:underline font-bold cursor-pointer"
                            >
                              Editar
                            </button>
                            <span className="text-slate-300">|</span>
                            <button 
                              onClick={() => handleDeleteClient(client.id)}
                              className="text-[10px] text-slate-450 hover:text-red-600 font-medium cursor-pointer"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Advanced Client Creation Form */}
              <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny h-fit space-y-3.5">
                <div className="flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <UserPlus className="h-4.5 w-4.5 text-brand-red" />
                  <h4 className="text-xs font-bold text-slate-900 uppercase">
                    {editingClient ? `Editar Cliente: ${editingClient.name}` : 'Crear Cliente y Asignar Centro'}
                  </h4>
                </div>

                <form onSubmit={handleCreateClientAdmin} className="space-y-3">
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Código del Cliente</label>
                    <input
                      type="text"
                      required
                      placeholder="E.g. CLI-ISN"
                      value={cCode}
                      onChange={(e) => setCCode(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red font-mono"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Razón Social</label>
                    <input
                      type="text"
                      required
                      placeholder="Grupo ISN de México"
                      value={cName}
                      onChange={(e) => setCName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">RFC Fiscal</label>
                    <input
                      type="text"
                      required
                      placeholder="GIS980312KL5"
                      value={cRfc}
                      onChange={(e) => setCRfc(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red font-mono"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Correo Contacto</label>
                    <input
                      type="email"
                      required
                      placeholder="contacto@isn.com.mx"
                      value={cEmail}
                      onChange={(e) => setCEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Teléfono Enlace</label>
                    <input
                      type="text"
                      required
                      placeholder="+52 449 123 4567"
                      value={cPhone}
                      onChange={(e) => setCPhone(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dirección Fiscal</label>
                    <input
                      type="text"
                      required
                      placeholder="Calle Principal, Aguascalientes, AGS"
                      value={cAddress}
                      onChange={(e) => setCAddress(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  {!editingClient && (
                    <div className="space-y-1.5 pt-1.5 border-t border-slate-100">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Vinculación de Centro de Costos</label>
                      <div className="grid grid-cols-3 gap-1 bg-slate-50 p-1 rounded-md border border-slate-200">
                        <button
                          type="button"
                          onClick={() => setAssignCcOption('none')}
                          className={`py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'none' ? 'bg-brand-dark text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                          Sin Centro
                        </button>
                        <button
                          type="button"
                          onClick={() => setAssignCcOption('existing')}
                          className={`py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'existing' ? 'bg-brand-dark text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                          Asignar Existente
                        </button>
                        <button
                          type="button"
                          onClick={() => setAssignCcOption('new')}
                          className={`py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${assignCcOption === 'new' ? 'bg-brand-dark text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                          Crear Nuevo
                        </button>
                      </div>

                      {assignCcOption === 'existing' && (
                        <div className="space-y-0.5 pt-1">
                          <label className="text-[9px] font-bold text-slate-500 uppercase">Seleccionar Centro de Costos</label>
                          <select
                            required
                            value={selectedCcId}
                            onChange={(e) => setSelectedCcId(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-800 focus:outline-hidden focus:border-brand-red"
                          >
                            <option value="">Seleccione el centro...</option>
                            {costCenters.map(cc => (
                              <option key={cc.id} value={cc.id}>{cc.name} ({cc.code})</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {assignCcOption === 'new' && (
                        <div className="space-y-2 pt-1.5 bg-slate-50 p-2.5 rounded-md border border-slate-200/50">
                          <div className="space-y-0.5">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Código del Centro</label>
                            <input
                              type="text"
                              required
                              placeholder="E.g. CC-AGS-01"
                              value={newCcCode}
                              onChange={(e) => setNewCcCode(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-855 font-mono"
                            />
                          </div>
                          <div className="space-y-0.5">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Nombre del Centro / Sucursal</label>
                            <input
                              type="text"
                              required
                              placeholder="Planta Aguascalientes"
                              value={newCcName}
                              onChange={(e) => setNewCcName(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-855"
                            />
                          </div>
                          <div className="space-y-0.5">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Gerente de Enlace</label>
                            <input
                              type="text"
                              placeholder="Ing. Alberto Gómez"
                              value={newCcManager}
                              onChange={(e) => setNewCcManager(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-855"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-tiny"
                    >
                      <Save className="h-3.5 w-3.5" /> 
                      <span>{editingClient ? 'Guardar Cambios' : 'Registrar Cliente'}</span>
                    </button>
                    {editingClient && (
                      <button
                        type="button"
                        onClick={() => setEditingClient(null)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </>
          )}

          {/* CREATE COST CENTERS SUBTAB */}
          {activeSubTab === 'costCenters' && (
            <>
              {/* Cost centers summary list */}
              <div className="xl:col-span-2 space-y-3">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Centros de Costos Registrados</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {costCenters.map(cc => {
                    const assignedClients = clients.filter(c => 
                      c.id === cc.clientId || 
                      c.code === cc.clientId || 
                      (cc.clientIds && (cc.clientIds.includes(c.id) || cc.clientIds.includes(c.code)))
                    );
                    return (
                      <div key={cc.id} className="bg-white p-3 rounded-lg border border-slate-200/80 shadow-tiny flex flex-col justify-between space-y-3 hover:border-slate-300 transition-all">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] bg-red-50 text-brand-red font-mono font-bold px-1.5 py-0.2 rounded-xs border border-red-100">
                              {cc.code}
                            </span>
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => setEditingCostCenter(cc)}
                                className="text-[10px] text-brand-red hover:underline font-bold cursor-pointer"
                              >
                                Editar
                              </button>
                              <span className="text-slate-300">|</span>
                              <button 
                                onClick={() => handleDeleteCostCenter(cc.id)}
                                className="text-[10px] text-slate-450 hover:text-red-600 font-medium cursor-pointer"
                              >
                                Eliminar
                              </button>
                            </div>
                          </div>
                          <h4 className="text-xs font-display font-bold text-slate-900 mt-1.5">{cc.name}</h4>
                          <p className="text-[11px] text-slate-500">📍 {cc.address}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Gerente: <strong className="font-bold text-slate-600">{cc.manager}</strong></p>
                          <p className="text-[9px] text-slate-500 font-mono mt-1 flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 w-fit">
                            <span>🗺️</span>
                            <span>{cc.lat ?? '25.6866'}, {cc.lng ?? '-100.3161'}</span>
                          </p>
                        </div>
                        <div className="border-t border-slate-100 pt-1.5 text-[9px] text-slate-500 font-bold uppercase">
                          <span className="text-slate-400 block mb-0.5">Cliente(s) Mandante(s):</span>
                          <div className="flex flex-wrap gap-1">
                            {assignedClients.length > 0 ? (
                              assignedClients.map(c => (
                                <span key={c.id} className="bg-blue-50 text-blue-800 border border-blue-200 font-bold px-1.5 py-0.5 rounded text-[9px]">
                                  🏢 {c.name}
                                </span>
                              ))
                            ) : (
                              <span className="bg-amber-50 text-amber-800 border border-amber-200/80 px-2 py-0.5 rounded text-[9px] font-bold">
                                🔒 Uso Exclusivo Interno / Staff
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Standard Cost Center Creation Form */}
              <div className="bg-white p-4 rounded-lg border border-slate-200/80 shadow-tiny h-fit space-y-3.5">
                <div className="flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <MapPin className="h-4.5 w-4.5 text-brand-red" />
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase">
                      {editingCostCenter ? `Editar Centro: ${editingCostCenter.name}` : 'Crear Centro de Costos'}
                    </h4>
                    <p className="text-[10px] text-slate-400">
                      Sede física o planta de operación (con o sin cliente mandante).
                    </p>
                  </div>
                </div>

                <form onSubmit={handleCreateCostCenterAdmin} className="space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                        <span>CLIENTE MANDANTE</span>
                        <span className="text-slate-400 font-normal lowercase">(opcional)</span>
                      </label>
                      <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded font-mono ${
                        ccSelectedClientIds.length === 0 
                          ? 'bg-amber-100 text-amber-900 border border-amber-200' 
                          : 'bg-blue-100 text-blue-900 border border-blue-200'
                      }`}>
                        {ccSelectedClientIds.length === 0 
                          ? '🔒 Sin Cliente (Interno)' 
                          : `${ccSelectedClientIds.length} seleccionado(s)`}
                      </span>
                    </div>

                    <p className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200/80 rounded-md p-2 leading-relaxed">
                      💡 <strong>Opcional:</strong> Si este centro de costos no tiene un cliente mandante (es de uso interno / staff de SNF), deja esta sección vacía. Al seleccionarlo en el sistema, <strong>únicamente se mostrará la carpeta de INTERNO</strong>.
                    </p>

                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[9px] text-slate-400 font-medium">Asignar clientes mandantes:</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setCcSelectedClientIds(clients.map(c => c.id))}
                          className="text-[9px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                        >
                          Seleccionar Todos
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCcSelectedClientIds([]);
                            setCcClientId('');
                          }}
                          className="text-[9px] font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                        >
                          Dejar Vacío (Interno)
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-1 bg-slate-50 p-2 rounded-md border border-slate-200 max-h-40 overflow-y-auto pr-1">
                      {clients.map(c => {
                        const isChecked = ccSelectedClientIds.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            onClick={() => toggleCcSelectedClient(c.id)}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs cursor-pointer transition-all ${
                              isChecked
                                ? 'bg-red-50 border-red-300 text-slate-900 font-bold'
                                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="h-3.5 w-3.5 text-brand-red rounded cursor-pointer"
                            />
                            <span className="truncate">🏢 {c.name} ({c.code})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Código del Centro</label>
                    <input
                      type="text"
                      required
                      placeholder="E.g. CC-SLT-FORJA"
                      value={ccCode}
                      onChange={(e) => setCcCode(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red font-mono"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre de Sucursal / Planta</label>
                    <input
                      type="text"
                      required
                      placeholder="Taller Soldadura Central"
                      value={ccName}
                      onChange={(e) => setCcName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Ingeniero Gerente</label>
                    <input
                      type="text"
                      required
                      placeholder="Ing. Carlos Garza"
                      value={ccManager}
                      onChange={(e) => setCcManager(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  <div className="space-y-0.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dirección de Operación</label>
                    <textarea
                      required
                      placeholder="Dirección física completa..."
                      value={ccAddress}
                      onChange={(e) => setCcAddress(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-850 placeholder-slate-400 focus:outline-hidden focus:border-brand-red h-14"
                    />
                  </div>

                  {/* COORDENADAS GPS PANEL */}
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                        <span>🗺️</span>
                        <span>Coordenadas Geográficas (Mapa / CEDIS)</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (pos) => {
                                setCcLat(Number(pos.coords.latitude.toFixed(6)));
                                setCcLng(Number(pos.coords.longitude.toFixed(6)));
                              },
                              () => {
                                alert('No se pudo obtener la ubicación GPS del navegador.');
                              }
                            );
                          }
                        }}
                        className="text-[9px] font-bold text-brand-red hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        📍 Ubicación Actual
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] font-semibold text-slate-500">Latitud</label>
                        <input
                          type="number"
                          step="any"
                          required
                          placeholder="25.6866"
                          value={ccLat}
                          onChange={(e) => setCcLat(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-850 font-mono focus:outline-hidden focus:border-brand-red"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-semibold text-slate-500">Longitud</label>
                        <input
                          type="number"
                          step="any"
                          required
                          placeholder="-100.3161"
                          value={ccLng}
                          onChange={(e) => setCcLng(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs text-slate-850 font-mono focus:outline-hidden focus:border-brand-red"
                        />
                      </div>
                    </div>

                    {/* Presets Rápidos */}
                    <div className="space-y-1 pt-1 border-t border-slate-200/60">
                      <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400 block">Atajos de Ubicación CEDIS:</span>
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => { setCcLat(25.6866); setCcLng(-100.3161); }}
                          className="text-[9px] bg-white hover:bg-slate-200 border border-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium cursor-pointer"
                        >
                          📍 MTY (25.6866, -100.3161)
                        </button>
                        <button
                          type="button"
                          onClick={() => { setCcLat(22.1565); setCcLng(-100.9855); }}
                          className="text-[9px] bg-white hover:bg-slate-200 border border-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium cursor-pointer"
                        >
                          📍 SLP (22.1565, -100.9855)
                        </button>
                        <button
                          type="button"
                          onClick={() => { setCcLat(25.5015); setCcLng(-100.8120); }}
                          className="text-[9px] bg-white hover:bg-slate-200 border border-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium cursor-pointer"
                        >
                          📍 Saltillo (25.5015, -100.8120)
                        </button>
                        <button
                          type="button"
                          onClick={() => { setCcLat(19.1234); setCcLng(-98.2456); }}
                          className="text-[9px] bg-white hover:bg-slate-200 border border-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium cursor-pointer"
                        >
                          📍 Puebla (19.1234, -98.2456)
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-tiny"
                    >
                      {editingCostCenter ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      <span>{editingCostCenter ? 'Guardar Cambios' : 'Registrar Centro'}</span>
                    </button>
                    {editingCostCenter && (
                      <button
                        type="button"
                        onClick={() => setEditingCostCenter(null)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </>
          )}

          {/* DESTINOS LOGISTICOS DE ENTREGA Y RUTAS */}
          {activeSubTab === 'destinos' && (
            <>
              {/* Destinations List */}
              <div className="xl:col-span-2 space-y-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-brand-red" /> Catálogo de Destinos y Rutas Logísticas
                    </h3>
                    <p className="text-[10px] text-slate-500">
                      Gestión centralizada de plantas de entrega, CEDIS y rutas registradas para asignación directa en folios y clientes.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-100 text-slate-700 font-mono text-[10px] font-bold px-2 py-0.5 rounded-full border border-slate-200">
                      {filteredDestinations.length} de {destinations.length} Destinos
                    </span>
                  </div>
                </div>

                {/* Filters Bar */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col sm:flex-row items-center gap-2.5">
                  <div className="w-full sm:w-1/2 flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-xs">
                    <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, dirección o notas..."
                      value={destSearchTerm}
                      onChange={(e) => setDestSearchTerm(e.target.value)}
                      className="w-full bg-transparent border-none outline-hidden text-xs text-slate-800 placeholder-slate-400"
                    />
                    {destSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setDestSearchTerm('')}
                        className="text-slate-400 hover:text-slate-600 text-xs font-bold px-1"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="w-full sm:w-1/2 flex items-center gap-1.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase shrink-0">Filtrar:</label>
                    <select
                      value={destClientFilter}
                      onChange={(e) => setDestClientFilter(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:outline-hidden focus:border-brand-red cursor-pointer"
                    >
                      <option value="ALL">🌐 Todos los Clientes y Destinos</option>
                      <option value="GENERAL">🌐 Solo Destinos Generales</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          🏢 {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="bg-white border border-slate-250/60 rounded-lg overflow-hidden shadow-tiny">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-600">
                      <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-700 tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3">Destino / Planta</th>
                          <th className="py-2.5 px-3">Cliente Asociado</th>
                          <th className="py-2.5 px-3">Coordenadas GPS</th>
                          <th className="py-2.5 px-3">Ruta / Distancia</th>
                          <th className="py-2.5 px-3 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {filteredDestinations.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-slate-400 text-xs">
                              {destSearchTerm || destClientFilter !== 'ALL'
                                ? 'No se encontraron destinos con el filtro seleccionado.'
                                : 'No hay destinos ni rutas registradas. Utiliza el formulario lateral para crear un nuevo destino.'}
                            </td>
                          </tr>
                        ) : (
                          filteredDestinations.map((dest) => {
                            const clientObj = clients.find(c => c.id === dest.clientId || (dest.clientIds && dest.clientIds.includes(c.id)));

                            return (
                              <tr key={dest.id} className="hover:bg-slate-50/80 transition-colors align-top">
                                <td className="py-2.5 px-3 font-semibold text-slate-900">
                                  <div className="flex flex-col space-y-0.5">
                                    <span className="text-xs text-brand-dark font-bold flex items-center gap-1.5">
                                      <MapPin className="h-3.5 w-3.5 text-brand-red shrink-0" />
                                      {dest.name}
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-normal line-clamp-1">
                                      {dest.address}
                                    </span>
                                  </div>
                                </td>

                                <td className="py-2.5 px-3">
                                  {dest.clientIds && dest.clientIds.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-w-xs">
                                      {dest.clientIds.map(cid => {
                                        const c = clients.find(cl => cl.id === cid || cl.code === cid);
                                        return (
                                          <span key={cid} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                            🏢 {c ? `${c.name} (${c.code})` : cid}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : clientObj ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                      🏢 {clientObj.name} ({clientObj.code})
                                    </span>
                                  ) : dest.clientName ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                      🏢 {dest.clientName}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      🌐 General (Todos los Clientes)
                                    </span>
                                  )}
                                </td>

                                <td className="py-2.5 px-3 font-mono text-[10px] text-slate-600">
                                  <span className="bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                    📍 {Number(dest.lat || 0).toFixed(4)}, {Number(dest.lng || 0).toFixed(4)}
                                  </span>
                                </td>

                                <td className="py-2.5 px-3">
                                  <div className="flex flex-col text-[10px]">
                                    <span className="font-bold text-slate-800 flex items-center gap-1">
                                      <Route className="h-3 w-3 text-emerald-600 shrink-0" />
                                      {dest.estimatedKm ? `${dest.estimatedKm} km` : '45 km'} • {dest.estimatedMins ? `${dest.estimatedMins} min` : '40 min'}
                                    </span>
                                    {dest.routeNotes && (
                                      <span className="text-[9px] text-slate-400 line-clamp-1 italic">
                                        {dest.routeNotes}
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td className="py-2.5 px-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => setEditingDestination(dest)}
                                      className="p-1 hover:bg-slate-100 rounded text-slate-600 hover:text-blue-600 transition-colors cursor-pointer"
                                      title="Editar Destino / Ruta"
                                    >
                                      <Edit2 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDestinationClick(dest.id)}
                                      className="p-1 hover:bg-red-50 rounded text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                                      title="Eliminar Destino"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Destination Form */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                    {editingDestination ? <Edit2 className="h-3.5 w-3.5 text-blue-600" /> : <Plus className="h-3.5 w-3.5 text-brand-red" />}
                    <span>{editingDestination ? 'Editar Destino / Ruta' : 'Alta de Nuevo Destino / Ruta'}</span>
                  </h3>
                  {editingDestination && (
                    <button
                      onClick={() => setEditingDestination(null)}
                      className="text-[10px] text-brand-red hover:underline font-bold cursor-pointer"
                    >
                      Cancelar Edición
                    </button>
                  )}
                </div>

                <form onSubmit={handleCreateDestination} className="bg-white border border-slate-250/60 rounded-lg p-3.5 space-y-3 shadow-tiny">
                  {/* Nombre del Destino */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between">
                      <span>Nombre del Destino / Planta *</span>
                      <span className="text-[9px] text-slate-400 font-normal">Identificador</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={destName}
                      onChange={(e) => setDestName(e.target.value)}
                      placeholder="Ej. Planta Ramos Arizpe, CEDIS Apodaca..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-semibold focus:bg-white focus:outline-hidden focus:border-brand-red"
                    />
                  </div>

                  {/* Socio Comercial / Cliente Asociado (Selección Múltiple) */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                        <span>Socio Comercial / Clientes Asociados</span>
                      </label>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        destSelectedClientIds.length === 0 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        {destSelectedClientIds.length === 0
                          ? '🌐 General (Todos)'
                          : `${destSelectedClientIds.length} ${destSelectedClientIds.length === 1 ? 'cliente' : 'clientes'}`}
                      </span>
                    </div>

                    {/* Quick Selection Buttons */}
                    <div className="flex items-center justify-between gap-1 text-[10px]">
                      <span className="text-slate-400 text-[10px]">Compartir destino con:</span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDestSelectedClientIds(clients.map(c => c.id))}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-bold hover:underline cursor-pointer"
                        >
                          Seleccionar Todos
                        </button>
                        <span className="text-slate-300">•</span>
                        <button
                          type="button"
                          onClick={() => setDestSelectedClientIds([])}
                          className="text-[10px] text-emerald-600 hover:text-emerald-800 font-bold hover:underline cursor-pointer"
                        >
                          🌐 Dejar General (Libre)
                        </button>
                      </div>
                    </div>

                    {/* Checkbox List */}
                    <div className="space-y-1 bg-slate-50 p-2 rounded-md border border-slate-200 max-h-44 overflow-y-auto pr-1">
                      {clients.length === 0 ? (
                        <p className="text-[11px] text-slate-400 text-center py-2">No hay clientes registrados aún.</p>
                      ) : (
                        clients.map(c => {
                          const isChecked = destSelectedClientIds.includes(c.id);
                          return (
                            <label
                              key={c.id}
                              onClick={() => toggleDestSelectedClient(c.id)}
                              className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-xs cursor-pointer transition-all ${
                                isChecked
                                  ? 'bg-blue-50 border-blue-300 text-blue-950 font-bold shadow-2xs'
                                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}}
                                  className="h-3.5 w-3.5 text-blue-600 rounded cursor-pointer"
                                />
                                <span className="truncate">🏢 {c.name} ({c.code})</span>
                              </div>
                              {isChecked && (
                                <span className="text-[9px] bg-blue-600 text-white font-bold px-1.5 py-0.5 rounded-full shrink-0">
                                  Asignado
                                </span>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>
                    <p className="text-[9.5px] text-slate-400 leading-tight">
                      * Puedes asignar este destino a 1 o más socios comerciales simultáneamente. Si no seleccionas ninguno, estará disponible como destino general.
                    </p>
                  </div>

                  {/* Dirección */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between">
                      <span>Dirección Completa de Destino *</span>
                    </label>
                    <textarea
                      rows={2}
                      required
                      value={destAddress}
                      onChange={(e) => setDestAddress(e.target.value)}
                      placeholder="Av. Universidad 1200, San Nicolás de los Garza, Nuevo León..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-semibold focus:bg-white focus:outline-hidden focus:border-brand-red resize-none"
                    />
                  </div>

                  {/* Coordenadas GPS */}
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                        <Compass className="h-3.5 w-3.5 text-brand-red" />
                        <span>Coordenadas GPS de Destino</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(pos => {
                              setDestLat(Number(pos.coords.latitude.toFixed(6)));
                              setDestLng(Number(pos.coords.longitude.toFixed(6)));
                            });
                          }
                        }}
                        className="text-[9px] text-blue-600 hover:underline font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <Compass className="h-3 w-3" /> Usar mi GPS
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[8.5px] font-bold text-slate-500 uppercase">Latitud</label>
                        <input
                          type="number"
                          step="any"
                          required
                          value={destLat}
                          onChange={(e) => setDestLat(parseFloat(e.target.value) || 0)}
                          className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[8.5px] font-bold text-slate-500 uppercase">Longitud</label>
                        <input
                          type="number"
                          step="any"
                          required
                          value={destLng}
                          onChange={(e) => setDestLng(parseFloat(e.target.value) || 0)}
                          className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-mono"
                        />
                      </div>
                    </div>

                    {/* Presets Rápidos */}
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-slate-200/60">
                      <button
                        type="button"
                        onClick={() => { setDestLat(25.6866); setDestLng(-100.3161); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        MTY Centro
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDestLat(25.5015); setDestLng(-100.8120); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        Ramos Arizpe
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDestLat(22.1565); setDestLng(-100.9855); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        SLP Planta
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDestLat(25.7820); setDestLng(-100.1850); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        Apodaca
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDestLat(20.5888); setDestLng(-100.3899); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        Querétaro
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDestLat(19.1234); setDestLng(-98.2456); }}
                        className="text-[9px] bg-white hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-mono border border-slate-200 cursor-pointer"
                      >
                        Puebla
                      </button>
                    </div>
                  </div>

                  {/* Route details */}
                  <div className="space-y-2 pt-1 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-700">Distancia Est. (km)</label>
                        <input
                          type="number"
                          value={destEstimatedKm}
                          onChange={(e) => setDestEstimatedKm(parseFloat(e.target.value) || 0)}
                          placeholder="45"
                          className="w-full bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-700">Tiempo Est. (mins)</label>
                        <input
                          type="number"
                          value={destEstimatedMins}
                          onChange={(e) => setDestEstimatedMins(parseFloat(e.target.value) || 0)}
                          placeholder="40"
                          className="w-full bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700">Puntos de Ruta / Notas de Acceso</label>
                      <input
                        type="text"
                        value={destRouteNotes}
                        onChange={(e) => setDestRouteNotes(e.target.value)}
                        placeholder="Ej. Autopista 57D -> Libramiento -> Caseta Lincoln..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs font-semibold"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex items-center gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white py-1.5 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-tiny"
                    >
                      {editingDestination ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      <span>{editingDestination ? 'Guardar Cambios' : 'Registrar Destino'}</span>
                    </button>
                    {editingDestination && (
                      <button
                        type="button"
                        onClick={() => setEditingDestination(null)}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </>
          )}

          {/* SYSTEM ACTIVITY TIMELINE */}
          {activeSubTab === 'activity' && (
            <div className="xl:col-span-3 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Bitácora de Sucesos y Actividad</h3>
                <span className="text-[10px] text-slate-400 font-mono">Últimos {logs.length} sucesos registrados</span>
              </div>

              <div className="bg-white border border-slate-250/60 rounded-lg overflow-hidden shadow-tiny">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-slate-600">
                    <thead className="bg-slate-550/10 text-slate-800 border-b border-slate-200 uppercase text-[9px] font-bold tracking-wider font-mono">
                      <tr>
                        <th className="p-3">Estampa Temporal</th>
                        <th className="p-3">Usuario Autor</th>
                        <th className="p-3">Acción Sucedida</th>
                        <th className="p-3">Detalle Técnico / Operación</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-sans">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50/50 transition-all">
                          <td className="p-3 font-mono text-[10px] text-slate-500 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="p-3 font-bold text-slate-800 whitespace-nowrap">
                            {log.userEmail}
                          </td>
                          <td className="p-3">
                            <span className={`text-[8.5px] font-bold font-mono px-1.5 py-0.5 rounded-sm uppercase ${
                              log.action.includes('LOGIN') || log.action.includes('SESION')
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-100'
                                : log.action.includes('FOLIO') 
                                  ? 'bg-amber-50 text-amber-700 border border-amber-100'
                                  : log.action.includes('CLIENTE')
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                    : 'bg-blue-50 text-blue-700 border border-blue-100'
                            }`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="p-3 text-[11px] text-slate-700">
                            {log.details}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* DATABASE CLEANUP SUBTAB */}
          {activeSubTab === 'database' && (
            <div className="xl:col-span-3 space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-tiny space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg border border-red-100">
                    <AlertCircle className="h-6 w-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-base font-bold text-slate-900">Limpieza General y Preparación de Producción</h3>
                    <p className="text-xs text-slate-500">
                      Esta herramienta permite vaciar completamente los datos de prueba preestablecidos del sistema para dejar la plataforma lista para la carga de información real.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">¿Qué colecciones serán vaciadas?</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 text-xs">
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Clientes Corporativos ({clients.length})</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Centros de Costos ({costCenters.length})</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Catálogo de Mercancías</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Folios de Operación</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Prefacturas Emitidas</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Sala de Meet Virtual</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Eventos de Logística</span>
                    </div>
                    <div className="bg-white border border-slate-150 p-2.5 rounded flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Historial de Actividades</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 italic mt-2">
                    Nota: Los perfiles de usuarios registrados (Administradores y Socios Comerciales) NO serán eliminados para evitar pérdida de accesos a la plataforma.
                  </p>
                </div>

                <div className="border-t border-slate-200 pt-5 space-y-4">
                  <div className="bg-amber-50/50 border border-amber-200 p-4 rounded-lg flex gap-3">
                    <Shield className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <h5 className="text-xs font-bold text-amber-900">Advertencia de Seguridad Crucial</h5>
                      <p className="text-[11px] text-amber-800 leading-relaxed">
                        Este proceso eliminará de forma irreversible toda la información almacenada tanto en la base de datos central en la nube de Firebase Firestore como en el caché local de todos los usuarios. No ejecute esta acción si la plataforma ya contiene registros reales en vivo.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={async () => {
                        const confirmFirst = await window.showAppConfirm(
                          "⚠️ ADVERTENCIA CRUCIAL ⚠️\n\n¿Está seguro de que desea eliminar todos los datos de prueba del sistema?\n\nEsta acción es irreversible y vaciará permanentemente las colecciones de clientes, centros de costos, catálogo de mercancías, folios de operación, prefacturas, videollamadas y actividades en tiempo real en la nube.",
                          "Peligro: Vaciar Base de Datos"
                        );
                        if (confirmFirst) {
                          const confirmSecond = await window.showAppConfirm(
                            "CONFIRMACIÓN SECUNDARIA\n\nPor favor confirme por segunda vez. ¿Desea dejar la aplicación 100% en blanco para ingresar información real?",
                            "Confirmación de Seguridad"
                          );
                          if (confirmSecond && onResetDatabase) {
                            onResetDatabase();
                          }
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-sm transition-all cursor-pointer flex items-center gap-2 border border-red-700"
                    >
                      <AlertCircle className="h-4 w-4" />
                      Vaciar Base de Datos / Eliminar Datos de Prueba
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WHATSAPP SUPPORT MANAGEMENT SUBTAB */}
          {activeSubTab === 'whatsapp' && (
            <div className="xl:col-span-3 space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                
                {/* Left Column: Management & Form */}
                <div className="lg:col-span-2 space-y-5">
                  
                  {/* Active Areas List */}
                  <div className="bg-white border border-slate-250/60 rounded-lg p-5 shadow-tiny space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 font-display">
                          <MessageCircle className="h-5 w-5 text-emerald-500" />
                          Líneas de Soporte Activas
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Departamentos configurados disponibles para comunicación directa en WhatsApp según cliente y almacén.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRestoreDefaultAreas}
                        className="text-[10px] font-bold text-slate-500 hover:text-brand-red flex items-center gap-1 border border-slate-200 hover:border-brand-red/30 px-2.5 py-1.5 rounded-md hover:bg-brand-red/5 transition-all cursor-pointer"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Restaurar Valores por Defecto
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {supportAreas.map((area) => (
                        <div 
                          key={area.id} 
                          className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-lg flex flex-col justify-between space-y-3 hover:border-slate-300 transition-all"
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className={`text-[9px] font-black px-2 py-0.5 rounded-sm uppercase tracking-wider font-mono border ${
                                area.color === 'emerald' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                area.color === 'blue' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                                area.color === 'indigo' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' :
                                area.color === 'amber' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                area.color === 'rose' ? 'bg-rose-50 text-rose-600 border-rose-200' :
                                'bg-violet-50 text-violet-600 border-violet-200'
                              }`}>
                                {area.id === 'facturacion' ? 'Administrativo' : area.id === 'almacen' ? 'Operativo' : 'Soporte Especializado'}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleEditSupportArea(area)}
                                  className="p-1 text-slate-400 hover:text-slate-900 hover:bg-white rounded border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                                  title="Editar"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSupportArea(area.id)}
                                  className="p-1 text-slate-400 hover:text-brand-red hover:bg-white rounded border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <h4 className="text-xs font-bold text-slate-900 font-sans tracking-tight leading-snug">{area.name}</h4>
                            <p className="text-[10px] text-slate-600 font-medium">Encargado: <span className="font-semibold text-slate-800">{area.agent}</span></p>
                            <p className="text-[9.5px] text-slate-450 font-mono flex items-center gap-1">
                              <Phone className="h-3 w-3 text-slate-400" />
                              +52 {area.phone.slice(0,3)} {area.phone.slice(3,6)} {area.phone.slice(6)}
                            </p>

                            {/* Client Scope Badges */}
                            <div className="pt-1">
                              <div className="flex items-center gap-1 text-[9px] font-bold text-slate-500 uppercase tracking-wider font-mono mb-1">
                                <Building className="h-3 w-3 text-blue-500" />
                                Cliente / Socio Asignado:
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {area.allClients || !area.clientIds || area.clientIds.length === 0 ? (
                                  <span className="text-[8.5px] font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded-sm font-mono border border-blue-200">
                                    🌐 Todos los Clientes (General)
                                  </span>
                                ) : (
                                  area.clientIds.map(cid => {
                                    const cObj = clients.find(c => c.id === cid || c.code === cid);
                                    return (
                                      <span key={cid} className="text-[8.5px] font-bold px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-sm font-mono border border-blue-200">
                                        🏢 {cObj?.name || cid} ({cObj?.code || cid})
                                      </span>
                                    );
                                  })
                                )}
                              </div>
                            </div>

                            {/* Cost Center Scope Badges */}
                            <div className="pt-1">
                              <div className="flex items-center gap-1 text-[9px] font-bold text-slate-500 uppercase tracking-wider font-mono mb-1">
                                <Building2 className="h-3 w-3 text-emerald-600" />
                                Centros de Costos Asignados:
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {area.allCostCenters || !area.costCenterIds || area.costCenterIds.length === 0 ? (
                                  <span className="text-[8.5px] font-bold px-2 py-0.5 bg-slate-200 text-slate-700 rounded-sm font-mono border border-slate-300">
                                    🌐 Cobertura Global (Todas las Sedes)
                                  </span>
                                ) : (
                                  area.costCenterIds.map(ccId => {
                                    const ccObj = costCenters.find(c => c.id === ccId || c.code === ccId);
                                    return (
                                      <span key={ccId} className="text-[8.5px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm font-mono border border-emerald-200">
                                        📍 {ccObj?.code?.replace('CC-','') || ccObj?.name || ccId}
                                      </span>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                          <p className="text-[9.5px] text-slate-500 border-t border-slate-200/50 pt-2 leading-relaxed italic truncate">
                            "{area.desc}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Creation / Editing Form */}
                  <div className="bg-white border border-slate-250/60 rounded-lg p-5 shadow-tiny space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 font-display">
                        <span className={`w-2.5 h-2.5 rounded-full block ${editingAreaId ? 'bg-amber-500 animate-pulse' : 'bg-brand-red'}`}></span>
                        {editingAreaId ? 'Editar Área de Soporte Existente' : 'Agregar Nueva Área de Soporte'}
                      </h3>
                      {editingAreaId && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingAreaId(null);
                            setAreaName('');
                            setAreaAgent('');
                            setAreaPhone('');
                            setAreaDesc('');
                            setAreaColor('emerald');
                            setAreaSelectedClientIds([]);
                            setAreaAllClients(true);
                            setAreaCostCenterIds([]);
                            setAreaAllCostCenters(true);
                          }}
                          className="text-[9px] font-black uppercase text-slate-400 hover:text-slate-900 tracking-wider transition-colors cursor-pointer"
                        >
                          [ Cancelar Edición ]
                        </button>
                      )}
                    </div>

                    <form onSubmit={handleSaveSupportArea} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre del Área / Departamento</label>
                          <input
                            type="text"
                            required
                            placeholder="Ej. Control de Almacén y Logística"
                            value={areaName}
                            onChange={(e) => setAreaName(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-800 focus:outline-hidden focus:border-brand-red focus:bg-white transition-all"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre del Encargado / Agente Responsable</label>
                          <input
                            type="text"
                            required
                            placeholder="Ej. Ing. Mateo Rodríguez (Encargado Almacén)"
                            value={areaAgent}
                            onChange={(e) => setAreaAgent(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-800 focus:outline-hidden focus:border-brand-red focus:bg-white transition-all"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
                            <span>Teléfono WhatsApp (10 dígitos)</span>
                            <span className="text-[9px] text-slate-400 normal-case">Solo números</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-semibold font-mono">+52</span>
                            <input
                              type="text"
                              required
                              maxLength={10}
                              pattern="\d{10}"
                              placeholder="Ej. 8712397902"
                              value={areaPhone}
                              onChange={(e) => setAreaPhone(e.target.value.replace(/\D/g, ''))}
                              className="w-full bg-slate-50 border border-slate-200 rounded-md pl-11 pr-3 py-2 text-xs text-slate-800 font-mono focus:outline-hidden focus:border-brand-red focus:bg-white transition-all"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Color / Tema de Identidad</label>
                          <select
                            value={areaColor}
                            onChange={(e) => setAreaColor(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-800 focus:outline-hidden focus:border-brand-red focus:bg-white transition-all"
                          >
                            <option value="emerald">Esmeralda (Verde)</option>
                            <option value="blue">Azul Ártico</option>
                            <option value="indigo">Indigo Profundo</option>
                            <option value="amber">Ámbar (Naranja)</option>
                            <option value="rose">Rosa Carmesí</option>
                            <option value="violet">Violeta Intenso</option>
                          </select>
                        </div>

                      </div>

                      {/* Client Association Multi-Select */}
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-2.5">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-blue-600 shrink-0" />
                            <div>
                              <label className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                                <span>Socio Comercial / Cliente(s) a Atender *</span>
                                <span className="text-[9px] bg-blue-100 text-blue-800 px-1.5 py-0.2 rounded font-mono font-bold">
                                  {areaAllClients ? 'Todos los Clientes' : `${areaSelectedClientIds.length} Seleccionado(s)`}
                                </span>
                              </label>
                              <p className="text-[10px] text-slate-500">
                                Indica a qué cliente(s) se les prestará servicio con este encargado. Solo aparecerá asignado a ellos en su portal.
                              </p>
                            </div>
                          </div>

                          {/* Quick selection helpers */}
                          {!areaAllClients && (
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => setAreaSelectedClientIds(clients.map(c => c.id))}
                                className="text-[10px] font-bold text-blue-700 hover:text-blue-800 bg-white border border-blue-200 hover:bg-blue-50 px-2 py-1 rounded cursor-pointer transition-colors"
                              >
                                Seleccionar Todos
                              </button>
                              <button
                                type="button"
                                onClick={() => setAreaSelectedClientIds([])}
                                className="text-[10px] font-bold text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:bg-slate-100 px-2 py-1 rounded cursor-pointer transition-colors"
                              >
                                Deseleccionar
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Checkbox: Atender a todos los clientes */}
                        <div className="flex items-center gap-2.5 bg-white p-2.5 rounded-md border border-slate-200">
                          <input
                            type="checkbox"
                            id="chk-all-clients-area"
                            checked={areaAllClients}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setAreaAllClients(checked);
                              if (checked) {
                                setAreaSelectedClientIds([]);
                              } else {
                                setAreaSelectedClientIds(clients.map(c => c.id));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                          />
                          <label htmlFor="chk-all-clients-area" className="text-xs font-bold text-slate-800 cursor-pointer select-none">
                            🌐 Cobertura General — Atender a <span className="underline decoration-blue-500">TODOS</span> los Socios Comerciales (Clientes)
                          </label>
                        </div>

                        {/* Multi-Selection Checkbox Grid when not global */}
                        {!areaAllClients && (
                          <div className="space-y-2 pt-1">
                            <p className="text-[10.5px] font-semibold text-slate-600">
                              Selecciona el cliente específico al que se le está prestando este servicio de atención y almacén:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                              {clients.map((c) => {
                                const isChecked = areaSelectedClientIds.includes(c.id);
                                return (
                                  <label
                                    key={c.id}
                                    className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-all ${
                                      isChecked
                                        ? 'bg-blue-50/90 border-blue-300 text-slate-900 shadow-xs'
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setAreaSelectedClientIds(prev => [...prev, c.id]);
                                        } else {
                                          setAreaSelectedClientIds(prev => prev.filter(id => id !== c.id));
                                        }
                                      }}
                                      className="h-4 w-4 mt-0.5 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <div className="min-w-0 text-left">
                                      <div className="text-xs font-bold truncate flex items-center gap-1.5">
                                        <span className="font-mono text-[10px] text-blue-700 bg-blue-100/80 px-1 rounded">
                                          {c.code}
                                        </span>
                                        <span className="truncate">{c.name}</span>
                                      </div>
                                      {c.rfc && (
                                        <div className="text-[9.5px] text-slate-400 truncate mt-0.5 font-mono">
                                          RFC: {c.rfc}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                            {areaSelectedClientIds.length === 0 && (
                              <p className="text-[10px] text-amber-600 font-bold bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                Debe seleccionar al menos un cliente o activar la cobertura general.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Cost Center Association Multi-Select */}
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-2.5">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-emerald-600 shrink-0" />
                            <div>
                              <label className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                                <span>Centro de Costos / Sedes a Atender</span>
                                <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded font-mono font-bold">
                                  {areaAllCostCenters ? 'Todas las Sedes' : `${areaCostCenterIds.length} Seleccionado(s)`}
                                </span>
                              </label>
                              <p className="text-[10px] text-slate-500">
                                Especifica si este encargado responderá a una o varias sedes/plantas operativas.
                              </p>
                            </div>
                          </div>

                          {/* Quick selection helpers */}
                          {!areaAllCostCenters && (
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => setAreaCostCenterIds(costCenters.map(c => c.id))}
                                className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-50 px-2 py-1 rounded cursor-pointer transition-colors"
                              >
                                Seleccionar Todos
                              </button>
                              <button
                                type="button"
                                onClick={() => setAreaCostCenterIds([])}
                                className="text-[10px] font-bold text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:bg-slate-100 px-2 py-1 rounded cursor-pointer transition-colors"
                              >
                                Deseleccionar
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Checkbox: Atender a todos los centros de costos */}
                        <div className="flex items-center gap-2.5 bg-white p-2.5 rounded-md border border-slate-200">
                          <input
                            type="checkbox"
                            id="chk-all-cost-centers"
                            checked={areaAllCostCenters}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setAreaAllCostCenters(checked);
                              if (checked) {
                                setAreaCostCenterIds([]);
                              } else {
                                setAreaCostCenterIds(costCenters.map(c => c.id));
                              }
                            }}
                            className="h-4 w-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                          />
                          <label htmlFor="chk-all-cost-centers" className="text-xs font-bold text-slate-800 cursor-pointer select-none">
                            🌐 Cobertura Global — Atender a <span className="underline decoration-emerald-500">TODOS</span> los Centros de Costos (Sedes)
                          </label>
                        </div>

                        {/* Multi-Selection Checkbox Grid when not global */}
                        {!areaAllCostCenters && (
                          <div className="space-y-2 pt-1">
                            <p className="text-[10.5px] font-semibold text-slate-600">
                              Selecciona los centros de costos específicos donde este personal atenderá:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                              {costCenters.map((cc) => {
                                const isChecked = areaCostCenterIds.includes(cc.id);
                                return (
                                  <label
                                    key={cc.id}
                                    className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-all ${
                                      isChecked
                                        ? 'bg-emerald-50/80 border-emerald-300 text-slate-900 shadow-xs'
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setAreaCostCenterIds(prev => [...prev, cc.id]);
                                        } else {
                                          setAreaCostCenterIds(prev => prev.filter(id => id !== cc.id));
                                        }
                                      }}
                                      className="h-4 w-4 mt-0.5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                                    />
                                    <div className="min-w-0 text-left">
                                      <div className="text-xs font-bold truncate flex items-center gap-1.5">
                                        <span className="font-mono text-[10px] text-emerald-700 bg-emerald-100/70 px-1 rounded">
                                          {cc.code}
                                        </span>
                                        <span className="truncate">{cc.name}</span>
                                      </div>
                                      {cc.address && (
                                        <div className="text-[9.5px] text-slate-400 truncate mt-0.5 font-sans">
                                          {cc.address}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>

                            {areaCostCenterIds.length === 0 && (
                              <p className="text-[10px] text-amber-600 font-bold bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                Debe seleccionar al menos un centro de costos o activar la cobertura global.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Descripción / Detalles de Atención</label>
                        <textarea
                          required
                          rows={2}
                          placeholder="Escriba las dudas específicas que atiende este departamento..."
                          value={areaDesc}
                          onChange={(e) => setAreaDesc(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-800 focus:outline-hidden focus:border-brand-red focus:bg-white transition-all resize-none"
                        />
                      </div>

                      <div className="flex justify-end pt-2">
                        <button
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-2.5 rounded-lg shadow-sm transition-all cursor-pointer flex items-center gap-2 border border-emerald-700"
                        >
                          <Save className="h-4 w-4" />
                          {editingAreaId ? 'Actualizar Área de Soporte' : 'Agregar Área de Soporte'}
                        </button>
                      </div>
                    </form>
                  </div>

                </div>

                {/* Right Column: Dynamic Live Preview */}
                <div className="bg-slate-900 border border-slate-950 rounded-lg p-5 text-white flex flex-col justify-between space-y-4 shadow-tiny">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5 font-mono">
                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                        Vista Previa en Vivo
                      </h3>
                      <span className="text-[9px] bg-slate-800 text-emerald-400 font-mono font-bold px-2 py-0.5 rounded">
                        Filtro Dinámico
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-normal">
                      Así visualizarán los socios comerciales sus accesos de WhatsApp directos según el cliente y sede activa:
                    </p>

                    {/* Interactive Filter Simulator: Client & Cost Center */}
                    <div className="space-y-2 pt-1">
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase font-mono block mb-1">
                          Simular Socio / Cliente Activo:
                        </label>
                        <select
                          value={previewFilterClientId}
                          onChange={(e) => setPreviewFilterClientId(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 text-blue-400 font-mono text-[10.5px] font-bold rounded p-1.5 focus:outline-none focus:border-blue-500"
                        >
                          <option value="ALL">🌐 Todos los Clientes (Sin Filtro de Cliente)</option>
                          {clients.map(c => (
                            <option key={c.id} value={c.id}>
                              🏢 {c.name} ({c.code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase font-mono block mb-1">
                          Simular Sede Activa:
                        </label>
                        <select
                          value={previewFilterCcId}
                          onChange={(e) => setPreviewFilterCcId(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 text-emerald-400 font-mono text-[10.5px] font-bold rounded p-1.5 focus:outline-none focus:border-emerald-500"
                        >
                          <option value="ALL">🌐 Todas las Sedes (Sin Filtro de Sede)</option>
                          {costCenters.map(cc => (
                            <option key={cc.id} value={cc.id}>
                              📍 {cc.name} ({cc.code})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 bg-slate-950 p-4 rounded-lg border border-slate-800 max-h-[380px] overflow-y-auto custom-scrollbar">
                    <div className="flex items-center justify-between text-xs font-bold text-slate-300 border-b border-slate-800 pb-2">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4 text-emerald-400" />
                        <span>Canales Visibles</span>
                      </div>
                      <span className="text-[9.5px] font-mono text-emerald-400 font-bold">
                        {(() => {
                          const selClient = previewFilterClientId === 'ALL' ? null : clients.find(c => c.id === previewFilterClientId);
                          const selCc = previewFilterCcId === 'ALL' ? null : costCenters.find(c => c.id === previewFilterCcId);
                          
                          const count = supportAreas.filter(area => {
                            // 1. Client filter check
                            if (selClient) {
                              let clientMatches = false;
                              if (area.allClients === true) {
                                clientMatches = true;
                              } else if (Array.isArray(area.clientIds) && area.clientIds.length > 0) {
                                const normCId = selClient.id.toLowerCase().trim();
                                const normCCode = selClient.code?.toLowerCase().trim();
                                const normCName = selClient.name?.toLowerCase().trim();
                                clientMatches = area.clientIds.some(cid => {
                                  const cnorm = cid.toLowerCase().trim();
                                  return cnorm === normCId || cnorm === normCCode || cnorm === normCName;
                                });
                              } else if (area.clientId) {
                                const normCId = selClient.id.toLowerCase().trim();
                                const normCCode = selClient.code?.toLowerCase().trim();
                                const normCName = selClient.name?.toLowerCase().trim();
                                const cnorm = area.clientId.toLowerCase().trim();
                                clientMatches = cnorm === normCId || cnorm === normCCode || cnorm === normCName;
                              } else if (area.allClients === undefined && (!area.clientIds || area.clientIds.length === 0) && !area.clientId) {
                                clientMatches = true;
                              }
                              if (!clientMatches) return false;
                            }

                            // 2. Cost Center filter check
                            if (selCc) {
                              let ccMatches = false;
                              if (area.allCostCenters === true) {
                                ccMatches = true;
                              } else if (Array.isArray(area.costCenterIds) && area.costCenterIds.length > 0) {
                                const normId = selCc.id.toLowerCase().trim();
                                const normCode = selCc.code?.toLowerCase().trim();
                                const normName = selCc.name?.toLowerCase().trim();
                                ccMatches = area.costCenterIds.some(cid => {
                                  const cnorm = cid.toLowerCase().trim();
                                  return cnorm === normId || cnorm === normCode || cnorm === normName;
                                });
                              } else if (area.allCostCenters === undefined && (!area.costCenterIds || area.costCenterIds.length === 0)) {
                                ccMatches = true;
                              }
                              if (!ccMatches) return false;
                            }

                            return true;
                          }).length;

                          return `${count} Líneas Visibles`;
                        })()}
                      </span>
                    </div>

                    {(() => {
                      const selClient = previewFilterClientId === 'ALL' ? null : clients.find(c => c.id === previewFilterClientId);
                      const selCc = previewFilterCcId === 'ALL' ? null : costCenters.find(c => c.id === previewFilterCcId);
                      
                      const filteredPreviewAreas = supportAreas.filter(area => {
                        // 1. Client filter check
                        if (selClient) {
                          let clientMatches = false;
                          if (area.allClients === true) {
                            clientMatches = true;
                          } else if (Array.isArray(area.clientIds) && area.clientIds.length > 0) {
                            const normCId = selClient.id.toLowerCase().trim();
                            const normCCode = selClient.code?.toLowerCase().trim();
                            const normCName = selClient.name?.toLowerCase().trim();
                            clientMatches = area.clientIds.some(cid => {
                              const cnorm = cid.toLowerCase().trim();
                              return cnorm === normCId || cnorm === normCCode || cnorm === normCName;
                            });
                          } else if (area.clientId) {
                            const normCId = selClient.id.toLowerCase().trim();
                            const normCCode = selClient.code?.toLowerCase().trim();
                            const normCName = selClient.name?.toLowerCase().trim();
                            const cnorm = area.clientId.toLowerCase().trim();
                            clientMatches = cnorm === normCId || cnorm === normCCode || cnorm === normCName;
                          } else if (area.allClients === undefined && (!area.clientIds || area.clientIds.length === 0) && !area.clientId) {
                            clientMatches = true;
                          }
                          if (!clientMatches) return false;
                        }

                        // 2. Cost Center filter check
                        if (selCc) {
                          let ccMatches = false;
                          if (area.allCostCenters === true) {
                            ccMatches = true;
                          } else if (Array.isArray(area.costCenterIds) && area.costCenterIds.length > 0) {
                            const normId = selCc.id.toLowerCase().trim();
                            const normCode = selCc.code?.toLowerCase().trim();
                            const normName = selCc.name?.toLowerCase().trim();
                            ccMatches = area.costCenterIds.some(cid => {
                              const cnorm = cid.toLowerCase().trim();
                              return cnorm === normId || cnorm === normCode || cnorm === normName;
                            });
                          } else if (area.allCostCenters === undefined && (!area.costCenterIds || area.costCenterIds.length === 0)) {
                            ccMatches = true;
                          }
                          if (!ccMatches) return false;
                        }

                        return true;
                      });

                      if (filteredPreviewAreas.length === 0) {
                        return (
                          <div className="p-4 text-center text-slate-400 space-y-1 bg-slate-900/60 rounded border border-dashed border-slate-800">
                            <p className="text-xs font-bold text-amber-400">Sin líneas disponibles</p>
                            <p className="text-[10px] text-slate-500">
                              Ningún departamento de soporte atiende actualmente los criterios seleccionados {selClient ? `(Cliente: ${selClient.name})` : ''} {selCc ? `(Sede: ${selCc.name})` : ''}.
                            </p>
                          </div>
                        );
                      }

                      return filteredPreviewAreas.map((area) => (
                        <div key={area.id} className="p-3 bg-slate-900 rounded-md border border-slate-850 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className={`text-[9px] font-bold uppercase tracking-wide ${
                              area.color === 'emerald' ? 'text-emerald-400' :
                              area.color === 'blue' ? 'text-blue-400' :
                              area.color === 'indigo' ? 'text-indigo-400' :
                              area.color === 'amber' ? 'text-amber-400' :
                              area.color === 'rose' ? 'text-rose-400' :
                              'text-violet-400'
                            }`}>{area.name}</span>
                            <span className="text-[8px] bg-slate-800 text-slate-400 px-1 py-0.2 rounded font-mono">Línea Directa</span>
                          </div>
                          <p className="text-[11px] font-semibold text-white">{area.agent}</p>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
                            <Phone className="h-3 w-3 text-slate-500" />
                            +52 {area.phone ? `${area.phone.slice(0,3)} ${area.phone.slice(3,6)} ${area.phone.slice(6)}` : 'S/N'}
                          </div>

                          {/* Preview Client Scope */}
                          <div className="pt-0.5">
                            {area.allClients || !area.clientIds || area.clientIds.length === 0 ? (
                              <span className="text-[8px] bg-blue-950/80 text-blue-300 border border-blue-800/80 px-1.5 py-0.2 rounded font-mono inline-block">
                                🌐 Todos los Clientes
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {area.clientIds.map(cid => {
                                  const cObj = clients.find(c => c.id === cid || c.code === cid);
                                  return (
                                    <span key={cid} className="text-[8px] bg-blue-950/80 text-blue-300 border border-blue-800/80 px-1 py-0.2 rounded font-mono">
                                      🏢 {cObj?.code || cObj?.name || cid}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Preview Cost Center Scope */}
                          <div className="pt-0.5">
                            {area.allCostCenters || !area.costCenterIds || area.costCenterIds.length === 0 ? (
                              <span className="text-[8px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-mono">
                                🌐 Todas las Sedes
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {area.costCenterIds.map(ccId => {
                                  const ccObj = costCenters.find(c => c.id === ccId || c.code === ccId);
                                  return (
                                    <span key={ccId} className="text-[8px] bg-emerald-950/80 text-emerald-300 border border-emerald-800/80 px-1 py-0.2 rounded font-mono">
                                      📍 {ccObj?.code?.replace('CC-','') || ccObj?.name || ccId}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <div className="text-[9px] text-slate-500 italic leading-snug pt-1 truncate">
                            {area.desc}
                          </div>
                          <a
                            href="#"
                            onClick={(e) => e.preventDefault()}
                            className={`inline-flex items-center gap-1.5 mt-2 text-slate-950 text-[10px] font-black px-2.5 py-1 rounded w-full justify-center transition-all cursor-pointer ${
                              area.color === 'emerald' ? 'bg-emerald-500 hover:bg-emerald-600' :
                              area.color === 'blue' ? 'bg-blue-500 hover:bg-blue-650' :
                              area.color === 'indigo' ? 'bg-indigo-500 hover:bg-indigo-600 text-white' :
                              area.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600' :
                              area.color === 'rose' ? 'bg-rose-500 hover:bg-rose-600 text-white' :
                              'bg-violet-500 hover:bg-violet-600 text-white'
                            }`}
                          >
                            <MessageCircle className="h-3 w-3 fill-current" />
                            Iniciar Chat WhatsApp
                          </a>
                        </div>
                      ));
                    })()}
                  </div>

                  <div className="text-[10px] text-slate-500 italic text-center pt-2 border-t border-slate-800">
                    Las conversaciones filtrarán automáticamente los encargados y contactos disponibles para el cliente y sede activa.
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
}
